import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { getApiBaseUrl, getUserDetailsBaseUrl, formatAccountLabel, formatPhoneLabel, eraseAllSessionData, notifyChatReset } from '../utils/chatIdentity';
import { getEmailAuthErrorMessage, getEmailValidationError, normalizeEmail } from '../utils/emailValidation';
import { enableWebPush, disableWebPush, getWebPushState, getPushPreference, setPushPreference } from '../utils/webPush';
import useOtpResendCooldown from '../hooks/useOtpResendCooldown';
import { ProfilePhotoActions, ProfilePhotoAvatar, useProfilePhotoEditor } from './ProfilePhotoEditor';
import InfoTooltip from './InfoTooltip';
import { lookupUsZip } from '../utils/shopAuthApi';
import { ZIP_INVALID_MESSAGE, getZipCodeError, normalizeZipCode } from '../utils/zipCode';
import { getProfileNameError, normalizeProfileName } from '../utils/profileName';
import { AddToHomeScreenCard } from './PwaInstallPrompt';
import { MARKETING_CONSENT_TEXT } from '../config/loginConsent';
import ConsentStatusCard from './profile/ConsentStatusCard';

const SkeletonBlock = ({ className = '' }) => (
    <div className={`animate-pulse rounded-3xl bg-slate-100 ${className}`} />
);

// "Saved" confirmation timing: visible for SAVED_VISIBLE_MS, then fades out over
// SAVED_FADE_MS (matches .animate-dash-fade-out) before the parent removes it.
const SAVED_VISIBLE_MS = 1800;
const SAVED_FADE_MS = 320;
const SAVED_CLEAR_MS = SAVED_VISIBLE_MS + SAVED_FADE_MS;

// Green self-drawing check + the existing "Saved" text. The parent still owns the
// message and clears it after SAVED_CLEAR_MS; this only switches to the fade-out
// for the last SAVED_FADE_MS. Give it a fresh `key` per save to replay the draw.
const SavedCheck = ({ text, as: Tag = 'span', className = '', ...rest }) => {
    const [leaving, setLeaving] = useState(false);

    useEffect(() => {
        const timer = window.setTimeout(() => setLeaving(true), SAVED_VISIBLE_MS);
        return () => window.clearTimeout(timer);
    }, []);

    return (
        <Tag {...rest} className={`inline-flex items-center gap-1.5 ${leaving ? 'animate-dash-fade-out' : ''} ${className}`}>
            <span className="animate-dash-intro-pop inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-[#BDE7D5] bg-[#ECFFF5] text-[#087A43]" aria-hidden="true">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round">
                    <path className="animate-dash-check-draw" pathLength="1" d="M5 12.5 9.5 17 19 7.5" />
                </svg>
            </span>
            {text}
        </Tag>
    );
};

const getVehicleLabel = (vehicle) => (
    [vehicle?.year, vehicle?.make, vehicle?.model].filter(Boolean).join(' ') || 'No active vehicle'
);

const DIY_LEVELS = [
    { key: 'beginner', label: 'Beginner', blurb: 'I want simple explanations and extra guidance.' },
    { key: 'intermediate', label: 'Intermediate', blurb: 'I can handle basic maintenance with clear steps.' },
    { key: 'advanced', label: 'Advanced', blurb: "I'm comfortable with tools, specs, and diagnostics." },
    { key: 'professional', label: 'Professional', blurb: 'I want technician-style guidance with minimal hand-holding.' },
];

const labelForLevel = (key) => DIY_LEVELS.find((l) => l.key === key)?.label || 'Intermediate';

const UNIT_PREFERENCE_ROWS = [
    {
        key: 'distance_unit',
        label: 'Mileage Units',
        options: [
            { value: 'mi', label: 'Miles' },
            { value: 'km', label: 'KM' },
        ],
    },
    {
        key: 'temperature_unit',
        label: 'Temperature',
        options: [
            { value: 'f', label: 'F' },
            { value: 'c', label: 'C' },
        ],
    },
    {
        key: 'fuel_volume_unit',
        label: 'Fuel Volume',
        options: [
            { value: 'gal', label: 'Gallons' },
            { value: 'l', label: 'Liters' },
        ],
    },
];

const DEFAULT_UNIT_PREFERENCES = {
    distance_unit: 'mi',
    temperature_unit: 'f',
    fuel_volume_unit: 'gal',
};

const DELETE_CONFIRMATION_PHRASE = 'delete my data';

const DiyKnowledgeLevelCard = ({ phone, isAuthenticated }) => {
    const [autoClassify, setAutoClassify] = useState(true);
    const [level, setLevel] = useState('intermediate');
    const [detected, setDetected] = useState('intermediate');
    const [loaded, setLoaded] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            if (!isAuthenticated || !phone) { setLoaded(true); return; }
            try {
                const resp = await fetch(`${getUserDetailsBaseUrl()}/api/diy_level/${encodeURIComponent(phone)}`);
                const data = await resp.json().catch(() => ({}));
                if (cancelled) return;
                const lvl = (data?.diy_level || 'intermediate').toLowerCase();
                setLevel(DIY_LEVELS.some((l) => l.key === lvl) ? lvl : 'intermediate');
                setDetected(DIY_LEVELS.some((l) => l.key === lvl) ? lvl : 'intermediate');
                setAutoClassify(data?.auto_classify !== false);
            } catch (_) {
                // keep defaults
            } finally {
                if (!cancelled) setLoaded(true);
            }
        };
        load();
        return () => { cancelled = true; };
    }, [phone, isAuthenticated]);

    const persist = useCallback(async (nextLevel, nextAuto) => {
        if (!isAuthenticated || !phone) return;
        setSaving(true);
        try {
            await fetch(`${getUserDetailsBaseUrl()}/api/diy_level`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ phone_number: phone, diy_level: nextLevel, auto_classify: nextAuto }),
            });
        } catch (_) {
            // best-effort; UI already reflects the choice
        } finally {
            setSaving(false);
        }
    }, [phone, isAuthenticated]);

    const handleToggleAuto = () => {
        const nextAuto = !autoClassify;
        setAutoClassify(nextAuto);
        // Turning auto OFF locks in the current (detected) level as the manual choice.
        const nextLevel = nextAuto ? level : (detected || level);
        if (!nextAuto) setLevel(nextLevel);
        persist(nextLevel, nextAuto);
    };

    const handlePickLevel = (key) => {
        // Manually picking a level always takes priority over auto-classification.
        setLevel(key);
        setAutoClassify(false);
        persist(key, false);
    };

    const activeKey = autoClassify ? detected : level;

    return (
        <section data-tooltip-boundary className="rounded-2xl border border-[#d9d9d9] bg-white p-5 shadow-sm">
            <div className="mb-4 flex items-center gap-2">
                <svg className="shrink-0" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#D81F26" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>
                {/* Inline flow keeps the "i" right after the last word, even when the heading wraps. */}
                <div className="min-w-0">
                    <h2 className="inline text-lg font-black leading-tight text-[#414141]">DIY Knowledge Level</h2>
                    <span className="ml-1 inline-flex align-middle">
                        <InfoTooltip
                            label="About DIY knowledge level"
                            text="Sets how detailed CarFido's guidance is. Turn on Auto-classify to let it adapt, or pick a level to lock it in."
                        />
                    </span>
                </div>
            </div>

            {/* Auto-classify */}
            <div className={`rounded-2xl border p-4 transition ${autoClassify ? 'border-[#d8d8d8] bg-[#f8f8f8]' : 'border-[#ececec] bg-white'}`}>
                <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-2">
                        <svg className="mt-0.5 shrink-0" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#414141" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v3m0 12v3M5.6 5.6l2.1 2.1m8.6 8.6 2.1 2.1M3 12h3m12 0h3M5.6 18.4l2.1-2.1m8.6-8.6 2.1-2.1"/></svg>
                        <div>
                            <p className="text-[15px] font-black text-[#414141]">Auto-classify</p>
                            {autoClassify && loaded && (
                                <p className="mt-1 text-sm font-bold text-[#6B6B6B]">Detected as {labelForLevel(detected)}</p>
                            )}
                        </div>
                    </div>
                    <button
                        type="button"
                        role="switch"
                        aria-checked={autoClassify}
                        onClick={handleToggleAuto}
                        disabled={saving}
                        className={`relative mt-1 h-7 w-12 shrink-0 rounded-full transition ${autoClassify ? 'bg-[#D81F26]' : 'bg-slate-300'} disabled:opacity-60`}
                    >
                        <span className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow transition-all ${autoClassify ? 'left-6' : 'left-1'}`} />
                    </button>
                </div>
            </div>

            {/* Manual levels */}
            <div className="mt-3 space-y-2">
                {DIY_LEVELS.map((opt) => {
                    const isActive = activeKey === opt.key;
                    const showAutoBadge = autoClassify && detected === opt.key;
                    return (
                        <button
                            key={opt.key}
                            type="button"
                            onClick={() => handlePickLevel(opt.key)}
                            disabled={saving}
                            className={`flex w-full items-start justify-between gap-4 rounded-2xl border p-4 text-left transition disabled:opacity-60 ${
                                isActive ? 'border-[#c7c7c7] bg-[#f8f8f8] ring-1 ring-[#d8d8d8]' : 'border-[#ececec] bg-white hover:bg-[#F9F9F9]'
                            }`}
                        >
                            <div>
                                <p className="text-[15px] font-black text-[#414141]">{opt.label}</p>
                                <p className="mt-0.5 text-sm font-medium text-[#414141]">{opt.blurb}</p>
                            </div>
                            {showAutoBadge && (
                                <span className="mt-0.5 shrink-0 rounded-full bg-[#F9F9F9] px-2.5 py-1 text-[11px] font-black text-[#414141]">Auto</span>
                            )}
                        </button>
                    );
                })}
            </div>

        </section>
    );
};

export const NotificationsCard = () => {
    const [state, setState] = useState({ supported: true, configured: false, permission: 'default', subscribed: false });
    const [pref, setPref] = useState(getPushPreference()); // 'on' by default (opt-out)
    const [loaded, setLoaded] = useState(false);
    const [busy, setBusy] = useState(false);
    const [notice, setNotice] = useState('');

    const refresh = useCallback(async () => {
        try {
            const s = await getWebPushState();
            setState(s);
        } catch (_) {
            // keep defaults
        } finally {
            setLoaded(true);
        }
    }, []);

    useEffect(() => { refresh(); }, [refresh]);

    const noticeForResult = useCallback((res) => {
        if (res?.ok) return "You're set. We'll notify you when it's time to check on a repair.";
        switch (res?.reason) {
            case 'denied': return 'Notifications are blocked. Allow them for this site in your browser settings, then try again.';
            case 'unauthenticated': return 'Please sign in again to enable notifications.';
            case 'not_configured': return "Notifications aren't available right now.";
            case 'unsupported': return "This browser doesn't support notifications.";
            case 'save_failed': return 'Could not save your notification setting. Please try again.';
            case 'dismissed': return 'Allow notifications in the browser prompt to finish turning this on.';
            default: return 'Could not enable notifications. Please try again.';
        }
    }, []);

    // Push is ON by default: unless the user has explicitly turned it off, activate it when they
    // land on Settings (prompts for browser permission at most once) and reconcile the toggle
    // with the real subscription state. Surfaces the reason if activation fails.
    useEffect(() => {
        if (getPushPreference() === 'off') return;
        let cancelled = false;
        (async () => {
            try {
                if (typeof window === 'undefined' || !('Notification' in window)) return;
                if (Notification.permission === 'denied') return;
                const res = await enableWebPush();
                if (cancelled) return;
                if (!res.ok && res.reason !== 'dismissed') setNotice(noticeForResult(res));
                refresh();
            } catch (_) { /* best effort */ }
        })();
        return () => { cancelled = true; };
    }, [refresh, noticeForResult]);

    const blocked = state.permission === 'denied';
    const optedOut = pref === 'off';
    // Truthful: the toggle is ON only when there's a real, granted subscription — so it can
    // never disagree with the browser's own permission state.
    const enabled = state.subscribed && state.permission === 'granted';

    const handleToggle = async () => {
        if (busy || blocked) return;
        setBusy(true);
        setNotice('');
        try {
            if (enabled) {
                // Turning OFF — remember the opt-out and unsubscribe this device.
                setPushPreference('off');
                setPref('off');
                await disableWebPush();
                setNotice('Notifications turned off.');
            } else {
                setPushPreference('on');
                setPref('on');
                const res = await enableWebPush();
                setNotice(noticeForResult(res));
            }
        } finally {
            await refresh();
            setBusy(false);
            window.setTimeout(() => setNotice(''), 5000);
        }
    };

    // Don't advertise a feature the browser can't do at all.
    if (loaded && !state.supported) return null;

    return (
        <section data-tooltip-boundary className="rounded-2xl border border-[#d9d9d9] bg-white p-5 shadow-sm">
            <div className="mb-4 flex items-center gap-2">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#D81F26" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
                    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                </svg>
                <h2 className="text-lg font-black text-[#414141]">Notifications</h2>
            </div>

            <div className={`rounded-2xl border p-4 transition ${enabled ? 'border-[#d8d8d8] bg-[#f8f8f8]' : 'border-[#ececec] bg-white'}`}>
                <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                        <div className="flex items-center gap-1">
                            <p className="text-[15px] font-black text-[#414141]">Repair follow-up reminders</p>
                            <InfoTooltip
                                label="About repair follow-up reminders"
                                text="Get a browser alert when CarFido follows up on a repair, even if this tab is closed."
                            />
                        </div>
                        {blocked && (
                            <p className="mt-2 text-sm font-bold text-[#b45309]">
                                Blocked in your browser. Allow notifications for this site in your browser settings to turn them on.
                            </p>
                        )}
                        {!blocked && !enabled && optedOut && (
                            <p className="mt-2 text-sm font-semibold text-[#414141]">
                                You turned these off. Toggle on to get repair follow-up reminders again.
                            </p>
                        )}
                    </div>
                    <button
                        type="button"
                        role="switch"
                        aria-checked={enabled}
                        onClick={handleToggle}
                        disabled={busy || blocked}
                        className={`relative mt-1 h-7 w-12 shrink-0 rounded-full transition ${enabled ? 'bg-[#D81F26]' : 'bg-slate-300'} disabled:cursor-not-allowed disabled:opacity-50`}
                    >
                        <span className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow transition-all ${enabled ? 'left-6' : 'left-1'}`} />
                    </button>
                </div>
                {/* When the browser has blocked notifications, the orange warning above already
                    explains what to do; don't repeat it with a second "blocked" notice. */}
                {notice && !blocked && <p className="mt-3 text-sm font-bold text-[#6B6B6B]">{notice}</p>}
            </div>
        </section>
    );
};

const UnitPreferencesCard = ({ phone, isAuthenticated, profile, onSaved }) => {
    const [preferences, setPreferences] = useState(DEFAULT_UNIT_PREFERENCES);
    const [savingKey, setSavingKey] = useState(null);
    const [status, setStatus] = useState('');
    // Bumped per save so a repeat "Saved" remounts SavedCheck and redraws the check.
    const [statusKey, setStatusKey] = useState(0);
    const statusTimerRef = useRef(null);

    useEffect(() => () => window.clearTimeout(statusTimerRef.current), []);

    useEffect(() => {
        setPreferences({
            distance_unit: profile?.distance_unit || DEFAULT_UNIT_PREFERENCES.distance_unit,
            temperature_unit: profile?.temperature_unit || DEFAULT_UNIT_PREFERENCES.temperature_unit,
            fuel_volume_unit: profile?.fuel_volume_unit || DEFAULT_UNIT_PREFERENCES.fuel_volume_unit,
        });
    }, [profile?.distance_unit, profile?.temperature_unit, profile?.fuel_volume_unit]);

    const handlePick = async (key, value) => {
        if (!isAuthenticated || !phone || preferences[key] === value) return;
        const previous = preferences;
        const next = { ...preferences, [key]: value };
        setPreferences(next);
        setSavingKey(key);
        window.clearTimeout(statusTimerRef.current);
        setStatus('');
        try {
            const response = await fetch(`${getUserDetailsBaseUrl()}/api/update_user_car`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ phone_number: phone, [key]: value }),
            });
            const payload = await response.json().catch(() => ({}));
            if (!response.ok || payload.status === 'error') {
                throw new Error(payload.error || 'Unable to save preferences.');
            }
            setStatus('Saved');
            setStatusKey((n) => n + 1);
            if (onSaved) onSaved();
        } catch (err) {
            setPreferences(previous);
            setStatus(err.message || 'Unable to save');
        } finally {
            setSavingKey(null);
            window.clearTimeout(statusTimerRef.current);
            statusTimerRef.current = window.setTimeout(() => setStatus(''), SAVED_CLEAR_MS);
        }
    };

    return (
        <section className="rounded-2xl border border-[#d9d9d9] bg-white p-5 shadow-sm">
            <div className="mb-4 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#D81F26" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M4 19V5" />
                        <path d="M8 19V9" />
                        <path d="M12 19V7" />
                        <path d="M16 19v-4" />
                        <path d="M20 19V11" />
                    </svg>
                    <h2 className="text-lg font-black text-[#414141]">Preferences</h2>
                </div>
                {status === 'Saved' ? (
                    <SavedCheck key={statusKey} text={status} className="text-xs font-black text-[#414141]" />
                ) : status && <span className="text-xs font-black text-[#414141]">{status}</span>}
            </div>

            <div className="space-y-3">
                {UNIT_PREFERENCE_ROWS.map((row) => (
                    <div key={row.key} className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <p className="text-sm font-black text-[#414141]">{row.label}</p>
                        <div className="grid min-h-[40px] grid-cols-2 rounded-full border border-[#d9d9d9] bg-[#F9F9F9] p-1 sm:min-w-[210px]">
                            {row.options.map((option) => {
                                const active = preferences[row.key] === option.value;
                                return (
                                    <button
                                        key={option.value}
                                        type="button"
                                        aria-pressed={active}
                                        disabled={savingKey === row.key}
                                        onClick={() => handlePick(row.key, option.value)}
                                        className={`rounded-full px-4 text-sm font-black transition disabled:opacity-60 ${
                                            active ? 'bg-[#414141] text-white shadow-sm' : 'text-[#414141] hover:bg-white'
                                        }`}
                                    >
                                        {savingKey === row.key && active ? 'Saving...' : option.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
};

// "Full Name" row in the profile details box, laid out exactly like the ZIP Code row
// beneath it (label · value · Edit) so every editable field is found the same way.
// In-place name editing for the profile header: "Edit Name" turns the name itself into
// a text field, and the header's Edit Name / Sign Out buttons become Save / Cancel.
export function useProfileNameEditor({ identity, profileName, onSaved }) {
    const [editing, setEditing] = useState(false);
    const [draftName, setDraftName] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const editButtonRef = useRef(null);
    const restoreFocusRef = useRef(false);

    const currentName = String(profileName || '').trim();

    // Return keyboard focus to Edit Name after Save/Cancel.
    useEffect(() => {
        if (!editing && restoreFocusRef.current && editButtonRef.current) {
            restoreFocusRef.current = false;
            editButtonRef.current.focus();
        }
    }, [editing]);

    const start = useCallback(() => {
        setDraftName(currentName);
        setError('');
        setEditing(true);
    }, [currentName]);

    const cancel = useCallback(() => {
        if (saving) return;
        restoreFocusRef.current = true;
        setError('');
        setEditing(false);
    }, [saving]);

    const save = useCallback(async () => {
        if (saving) return;
        const nextName = normalizeProfileName(draftName);
        if (!identity) {
            setError('Sign in again before saving your name.');
            return;
        }
        const nameError = getProfileNameError(nextName);
        if (nameError) {
            setError(nameError);
            return;
        }
        if (nextName.length > 120) {
            setError('Name must be 120 characters or less.');
            return;
        }
        if (nextName === currentName) {
            cancel();
            return;
        }

        setSaving(true);
        setError('');
        try {
            const response = await fetch(`${getUserDetailsBaseUrl()}/api/update_user_car`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ phone_number: identity, name: nextName }),
            });
            const payload = await response.json().catch(() => ({}));
            if (!response.ok || payload.status === 'error') {
                throw new Error(payload.error || payload.message || 'Unable to save your name.');
            }
            restoreFocusRef.current = true;
            setEditing(false);
            if (onSaved) await onSaved(nextName);
        } catch (err) {
            setError(err.message || 'Unable to save your name.');
        } finally {
            setSaving(false);
        }
    }, [cancel, currentName, draftName, identity, onSaved, saving]);

    const onDraftChange = useCallback((value) => {
        setDraftName(value);
        setError('');
    }, []);

    return { editing, draftName, saving, error, editButtonRef, start, cancel, save, onDraftChange };
}

// The header name: plain text, or (while editing) a text field in the same place and size.
export const ProfileNameField = ({ editor, displayName }) => {
    if (!editor.editing) {
        return <p className="truncate text-xl font-black leading-tight text-[#414141]">{displayName}</p>;
    }
    return (
        <div>
            <label htmlFor="settings-full-name" className="sr-only">Full name</label>
            <input
                id="settings-full-name"
                type="text"
                autoComplete="name"
                value={editor.draftName}
                onChange={(e) => editor.onDraftChange(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); editor.save(); }
                    if (e.key === 'Escape') { e.preventDefault(); editor.cancel(); }
                }}
                disabled={editor.saving}
                aria-invalid={Boolean(editor.error)}
                aria-describedby={editor.error ? 'settings-full-name-error' : undefined}
                maxLength={120}
                placeholder="Full name"
                autoFocus
                className={`-ml-2 block w-[calc(100%+0.5rem)] min-w-0 rounded-lg border-2 bg-white px-2 py-0.5 text-xl font-black leading-tight text-[#414141] outline-none transition placeholder:font-bold placeholder:text-[#b0b0b0] focus:ring-4 disabled:opacity-70 ${
                    editor.error ? 'border-red-400 focus:ring-red-100' : 'border-[#D81F26] focus:ring-[#D81F26]/15'
                }`}
            />
            {editor.error && <p id="settings-full-name-error" role="alert" className="mt-1 text-xs font-bold text-red-600">{editor.error}</p>}
        </div>
    );
};

// While the ZIP code is being edited its summary row is hidden, so the label and
// value are not shown twice; the row comes back on Save/Cancel.
export const ProfileDetailsEditor = ({ identity, region, profileLoading, onSaved, bare = false }) => {
    const [expanded, setExpanded] = useState(false);
    const [draftRegion, setDraftRegion] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [noticeKey, setNoticeKey] = useState(0);
    const noticeTimerRef = useRef(null);

    useEffect(() => () => window.clearTimeout(noticeTimerRef.current), []);

    const currentRegion = String(region || '').trim();
    // Accounts saved before the field became a ZIP code may hold a state or city
    // ("New Jersey"). Show it, ask for a ZIP, and never pre-fill it into the ZIP input.
    const currentZip = normalizeZipCode(currentRegion);
    const needsZipUpdate = Boolean(currentRegion) && !currentZip;

    useEffect(() => {
        if (expanded) return;
        setDraftRegion(currentZip);
    }, [currentZip, expanded]);

    const openEditor = () => {
        setDraftRegion(currentZip);
        setError('');
        setNotice('');
        setExpanded(true);
    };

    const closeEditor = () => {
        setExpanded(false);
        setError('');
        setNotice('');
        setDraftRegion(currentZip);
    };

    const handleSave = async () => {
        if (saving) return;
        if (!identity) {
            setError('Sign in again before saving profile details.');
            return;
        }

        // The field is a ZIP code (users.region); older free-text values such as
        // "New Jersey" still display, but a save must be a valid ZIP.
        const zipError = getZipCodeError(draftRegion);
        if (zipError) {
            setError(zipError);
            return;
        }
        const nextRegion = normalizeZipCode(draftRegion);

        setSaving(true);
        setError('');
        setNotice('');
        try {
            try {
                const info = await lookupUsZip(nextRegion);
                if (info?.zip !== nextRegion) throw new Error(ZIP_INVALID_MESSAGE);
            } catch (lookupError) {
                if (lookupError?.status === 404 || lookupError?.status === 400) {
                    throw new Error(ZIP_INVALID_MESSAGE);
                }
                throw lookupError;
            }
            if (nextRegion === currentRegion) {
                setExpanded(false);
                return;
            }
            const response = await fetch(`${getUserDetailsBaseUrl()}/api/update_user_car`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    phone_number: identity,
                    region: nextRegion,
                }),
            });
            const payload = await response.json().catch(() => ({}));
            if (!response.ok || payload.status === 'error') {
                throw new Error(payload.error || payload.message || 'Unable to save profile details.');
            }
            window.clearTimeout(noticeTimerRef.current);
            setNotice('Saved');
            setNoticeKey((n) => n + 1);
            setExpanded(false);
            if (onSaved) await onSaved();
            noticeTimerRef.current = window.setTimeout(() => setNotice(''), SAVED_CLEAR_MS);
        } catch (err) {
            setError(err.message || 'Unable to save profile details.');
        } finally {
            setSaving(false);
        }
    };

    const inputClass = 'h-11 w-full rounded-xl border border-[#d9d9d9] bg-white px-3.5 text-sm font-bold text-[#414141] outline-none transition placeholder:font-medium placeholder:text-[#b0b0b0] focus:border-[#D81F26] focus:ring-4 focus:ring-[#D81F26]/10';

    return (
        <div className={bare ? '' : 'mt-5 overflow-hidden rounded-2xl border border-[#ececec] bg-white'}>
            <div className="divide-y divide-[#d9d9d9]">
                <div className={`grid grid-cols-[1fr_auto] gap-x-3 gap-y-1 px-4 py-3 card-wide:grid-cols-[150px_1fr_auto] card-wide:items-center ${expanded ? 'hidden' : ''}`}>
                    <p className="text-sm font-black text-[#414141]">ZIP Code</p>
                    <div className="min-w-0 card-wide:col-start-2 card-wide:row-start-1">
                        <p className="truncate text-sm font-semibold text-[#414141]">
                            {profileLoading ? 'Loading...' : (currentRegion || 'Not set')}
                        </p>
                        {!profileLoading && needsZipUpdate && (
                            <p className="mt-0.5 text-xs font-bold text-amber-700">Please update this to your 5-digit ZIP code.</p>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={openEditor}
                        disabled={profileLoading || saving}
                        className="col-start-2 row-span-2 row-start-1 inline-flex min-h-[34px] items-center justify-center self-center rounded-full border border-[#d9d9d9] bg-white px-4 text-xs font-black text-[#414141] transition hover:border-[#D81F26] hover:text-[#D81F26] disabled:opacity-60 card-wide:col-start-3 card-wide:row-span-1 card-wide:row-start-1"
                    >
                        Edit
                    </button>
                </div>
            </div>

            <div className={`dash-collapsible ${expanded ? 'is-open' : ''}`}>
                <div>
                    <div className="bg-[#f8f8f8] p-4">
                        <label htmlFor="settings-zip-code" className="block text-xs font-black uppercase tracking-[0.12em] text-[#414141]">ZIP Code</label>
                        <div>
                            <input
                                id="settings-zip-code"
                                type="text"
                                inputMode="numeric"
                                autoComplete="postal-code"
                                aria-invalid={Boolean(error)}
                                aria-describedby={error ? 'settings-zip-error settings-zip-hint' : 'settings-zip-hint'}
                                value={draftRegion}
                                disabled={saving}
                                onChange={(e) => { setDraftRegion(e.target.value); if (error) setError(''); }}
                                onKeyDown={(e) => { if (e.key === 'Enter' && !saving) { e.preventDefault(); handleSave(); } }}
                                placeholder="e.g. 07030"
                                className={`${inputClass} mt-1.5`}
                            />
                            <p id="settings-zip-hint" className="mt-1.5 text-xs font-semibold text-[#414141]">Used to find repair shops near you.</p>
                        </div>
                        {error && <p id="settings-zip-error" role="alert" className="mt-2 text-xs font-bold text-red-600">{error}</p>}
                        {!error && notice && <SavedCheck key={noticeKey} as="p" text={notice} className="mt-2 text-xs font-bold text-[#6B6B6B]" />}
                        <div className="mt-4 flex flex-col gap-2 card-wide:flex-row card-wide:justify-end">
                            <button
                                type="button"
                                onClick={closeEditor}
                                disabled={saving}
                                className="inline-flex h-11 items-center justify-center rounded-xl border border-[#d9d9d9] bg-white px-5 text-sm font-black text-[#414141] transition hover:bg-[#F9F9F9] disabled:opacity-60"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleSave}
                                disabled={saving}
                                className="inline-flex h-11 items-center justify-center rounded-xl bg-[#D81F26] px-5 text-sm font-black text-white transition hover:bg-[#B8181E] active:scale-[0.99] disabled:opacity-60"
                            >
                                {saving ? 'Saving...' : 'Save'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Repair-shop search radius (#697 S-62). Stored on the profile in whole miles via the
// same update endpoint as the other preferences; the server re-validates 1-100.
export const SHOP_SEARCH_RADIUS_PRESETS = [5, 10, 25, 50];
export const SHOP_SEARCH_RADIUS_DEFAULT = 50;
const SHOP_SEARCH_RADIUS_MIN = 1;
const SHOP_SEARCH_RADIUS_MAX = 100;
const SHOP_SEARCH_RADIUS_ERROR = `Enter a whole number of miles between ${SHOP_SEARCH_RADIUS_MIN} and ${SHOP_SEARCH_RADIUS_MAX}.`;

const parseRadius = (value) => {
    const text = String(value ?? '').trim();
    if (!/^\d+$/.test(text)) return null;
    const miles = Number(text);
    return miles >= SHOP_SEARCH_RADIUS_MIN && miles <= SHOP_SEARCH_RADIUS_MAX ? miles : null;
};

const radiusChoiceFor = (miles) => (SHOP_SEARCH_RADIUS_PRESETS.includes(miles) ? String(miles) : 'custom');

export const ShopSearchRadiusSelector = ({ identity, savedRadius, profileLoading, onSaved }) => {
    const saved = parseRadius(savedRadius) || SHOP_SEARCH_RADIUS_DEFAULT;
    const [choice, setChoice] = useState(radiusChoiceFor(saved));
    const [customValue, setCustomValue] = useState(radiusChoiceFor(saved) === 'custom' ? String(saved) : '');
    // Custom slot: input + Apply while editing; once applied it settles into a
    // selected "Custom · 25 mi" pill (tap it to change the value again).
    const [customEditing, setCustomEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [noticeKey, setNoticeKey] = useState(0);

    // Follow the saved value when the profile (re)loads.
    useEffect(() => {
        setChoice(radiusChoiceFor(saved));
        if (radiusChoiceFor(saved) === 'custom') setCustomValue(String(saved));
        setCustomEditing(false);
        setError('');
    }, [saved]);

    useEffect(() => {
        if (!notice) return undefined;
        const timer = window.setTimeout(() => setNotice(''), SAVED_CLEAR_MS);
        return () => window.clearTimeout(timer);
    }, [notice, noticeKey]);

    const saveRadius = async (miles, { revertTo, onSuccess } = {}) => {
        if (!identity) {
            setError('Sign in again before saving your search radius.');
            return;
        }
        setSaving(true);
        setError('');
        setNotice('');
        try {
            const response = await fetch(`${getUserDetailsBaseUrl()}/api/update_user_car`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ phone_number: identity, shop_search_radius_miles: miles }),
            });
            const payload = await response.json().catch(() => ({}));
            if (!response.ok || payload.status === 'error') {
                throw new Error(payload.error || payload.message || 'Unable to save your search radius.');
            }
            setNotice('Saved.');
            setNoticeKey((n) => n + 1);
            if (onSuccess) onSuccess();
            if (onSaved) await onSaved();
        } catch (err) {
            if (revertTo) setChoice(revertTo);
            setError(err.message || 'Unable to save your search radius.');
        } finally {
            setSaving(false);
        }
    };

    const pickPreset = (miles) => {
        if (saving) return;
        const previous = choice;
        setChoice(String(miles));
        setCustomEditing(false);
        setError('');
        if (miles !== saved) saveRadius(miles, { revertTo: previous });
    };

    const pickCustom = () => {
        if (saving) return;
        setChoice('custom');
        setCustomEditing(true);
        setError('');
        if (!customValue && radiusChoiceFor(saved) === 'custom') setCustomValue(String(saved));
    };

    // Escape while typing: go back to whatever is saved, without saving anything.
    const cancelCustom = () => {
        if (saving) return;
        const savedChoice = radiusChoiceFor(saved);
        setChoice(savedChoice);
        setCustomValue(savedChoice === 'custom' ? String(saved) : '');
        setCustomEditing(false);
        setError('');
    };

    const applyCustom = () => {
        if (saving) return;
        const miles = parseRadius(customValue);
        if (miles === null) {
            setError(SHOP_SEARCH_RADIUS_ERROR);
            return;
        }
        setCustomValue(String(miles));
        if (miles === saved) {
            setCustomEditing(false);
            setNotice('Saved.');
            setNoticeKey((n) => n + 1);
            return;
        }
        saveRadius(miles, { onSuccess: () => setCustomEditing(false) });
    };

    const disabled = saving || (profileLoading && savedRadius == null);
    const isCustom = choice === 'custom';
    const pillClass = (active) => `inline-flex h-10 min-w-0 items-center justify-center whitespace-nowrap rounded-full border px-2 text-sm font-black transition active:scale-[0.98] disabled:cursor-default disabled:opacity-60 ${
        active
            ? 'border-[#414141] bg-[#414141] text-white shadow-sm'
            : 'border-[#d9d9d9] bg-white text-[#414141] hover:bg-[#f8f8f8] hover:border-[#414141]'
    }`;

    // Feedback line: shown only when there is an error or "Saved." to report.
    let message = null;
    if (error) message = { id: 'shop-search-radius-error', text: error, className: 'font-bold text-red-600', role: 'alert' };
    else if (notice) message = { id: 'shop-search-radius-status', text: notice, className: 'font-bold text-emerald-700', role: 'status' };

    // Custom pill: "Custom" until a custom radius is applied, then that value
    // (tap it to change). The miles input + Apply sit on their own row below.
    const customApplied = isCustom && !customEditing && customValue;

    return (
        <div className="px-4 py-3">
            <div className="flex items-center gap-1">
                <p id="shop-search-radius-label" className="text-sm font-black text-[#414141]">Search Radius</p>
                <InfoTooltip
                    label="About search radius"
                    text="How far from your ZIP code CarFido searches for repair shops."
                />
            </div>

            <div className="mt-3 max-w-md">
                <div role="group" aria-labelledby="shop-search-radius-label" className="grid grid-cols-3 gap-2 card-wide:grid-cols-4">
                    {SHOP_SEARCH_RADIUS_PRESETS.map((miles) => (
                        <button
                            key={miles}
                            type="button"
                            aria-pressed={choice === String(miles)}
                            disabled={disabled}
                            onClick={() => pickPreset(miles)}
                            className={pillClass(choice === String(miles))}
                        >
                            {miles} mi
                        </button>
                    ))}
                    {customApplied ? (
                        <button
                            type="button"
                            aria-pressed
                            aria-label={`Custom · ${customValue} mi`}
                            title="Change custom radius"
                            disabled={disabled}
                            onClick={() => { setCustomEditing(true); setError(''); }}
                            className={`${pillClass(true)} col-span-3 card-wide:col-span-1`}
                        >
                            Custom · {customValue} mi
                        </button>
                    ) : (
                        <button
                            type="button"
                            aria-pressed={isCustom}
                            disabled={disabled}
                            onClick={pickCustom}
                            className={`${pillClass(isCustom)} col-span-3 card-wide:col-span-1`}
                        >
                            Custom
                        </button>
                    )}
                </div>

                {isCustom && customEditing && (
                    <div className="mt-3 flex items-center gap-2">
                        <label htmlFor="shop-search-radius-custom" className="sr-only">Custom search radius in miles</label>
                        <div className="relative min-w-0 flex-1">
                            <input
                                id="shop-search-radius-custom"
                                type="text"
                                inputMode="numeric"
                                value={customValue}
                                onChange={(e) => { setCustomValue(e.target.value.replace(/\D/g, '').slice(0, 3)); if (error) setError(''); }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') { e.preventDefault(); applyCustom(); }
                                    if (e.key === 'Escape') { e.preventDefault(); cancelCustom(); }
                                }}
                                disabled={saving}
                                aria-invalid={Boolean(error)}
                                aria-describedby={message?.id}
                                placeholder="e.g. 25"
                                autoFocus
                                className={`h-11 w-full rounded-full border bg-white pl-5 pr-12 text-sm font-bold text-[#414141] outline-none transition placeholder:font-medium placeholder:text-[#b0b0b0] focus:ring-4 ${
                                    error ? 'border-red-300 focus:border-red-400 focus:ring-red-100' : 'border-[#d9d9d9] focus:border-[#D81F26] focus:ring-[#D81F26]/10'
                                }`}
                            />
                            <span className="pointer-events-none absolute right-5 top-1/2 -translate-y-1/2 text-xs font-black text-[#b0b0b0]" aria-hidden="true">mi</span>
                        </div>
                        <button
                            type="button"
                            onClick={applyCustom}
                            disabled={saving}
                            className="inline-flex h-11 shrink-0 items-center justify-center rounded-full bg-[#D81F26] px-6 text-sm font-black text-white shadow-sm transition hover:bg-[#B8181E] active:scale-[0.99] disabled:opacity-60"
                        >
                            {saving ? 'Saving...' : 'Apply'}
                        </button>
                    </div>
                )}

                {message && (message.role === 'status' ? (
                    <SavedCheck key={noticeKey} as="p" id={message.id} role={message.role} text={message.text} className={`mt-3 text-xs font-semibold leading-relaxed ${message.className}`} />
                ) : (
                    <p id={message.id} role={message.role} className={`mt-3 text-xs font-semibold leading-relaxed ${message.className}`}>
                        {message.text}
                    </p>
                ))}
            </div>
        </div>
    );
};

const OTP_LENGTH = 6;

// Shown when a verified identifier turns out to belong to a second account. Both
// identities are already proven at this point — the user is signed into one and has
// just passed a code sent to the other — so combining them is legitimate. It still
// needs an explicit yes, and it names what will move so the choice is informed.
const MergeConfirmPrompt = ({ preview, busy, onConfirm, onCancel, what }) => {
    const items = [
        ['vehicle', preview?.vehicles],
        ['saved memory', preview?.memories],
        ['document', preview?.documents],
        ['diagnostic ticket', preview?.tickets],
    ].filter(([, count]) => Number(count) > 0)
        .map(([label, count]) => `${count} ${label}${count === 1 ? '' : 's'}`);

    return (
        <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3.5">
            <p className="text-[14px] font-black text-[#414141]">
                That {what} belongs to another account
            </p>
            <p className="mt-1 text-[13px] font-medium leading-relaxed text-[#414141]">
                {items.length
                    ? `That account has ${items.join(', ')}. Combining moves everything into this account. Nothing is deleted.`
                    : 'That account is empty. Combining it will simply free up this ' +
                      `${what} for your account. Nothing is deleted.`}
            </p>
            <div className="mt-3 flex flex-col gap-2 card-wide:flex-row">
                <button
                    type="button"
                    onClick={onConfirm}
                    disabled={busy}
                    className="inline-flex h-11 items-center justify-center rounded-xl bg-[#D81F26] px-5 text-sm font-black text-white transition hover:bg-[#B8181E] active:scale-[0.99] disabled:opacity-60"
                >
                    {busy ? 'Combining...' : 'Combine accounts'}
                </button>
                <button
                    type="button"
                    onClick={onCancel}
                    disabled={busy}
                    className="inline-flex h-11 items-center justify-center rounded-xl border border-[#d9d9d9] bg-white px-5 text-sm font-black text-[#414141] transition hover:bg-[#F9F9F9] disabled:opacity-60"
                >
                    Use a different {what}
                </button>
            </div>
        </div>
    );
};

const ChangeContactEditor = ({ contactType, currentValue, onSaved, onCancel }) => {
    const [step, setStep] = useState('current_send');
    const [currentOtp, setCurrentOtp] = useState('');
    const [newOtp, setNewOtp] = useState('');
    const [newEmail, setNewEmail] = useState('');
    const [newPhone, setNewPhone] = useState('');
    const [phoneCountry, setPhoneCountry] = useState(null);
    const [pendingNewValue, setPendingNewValue] = useState('');
    const [changeToken, setChangeToken] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);
    const {
        secondsRemaining: currentResendSeconds,
        isCoolingDown: isCurrentResendCoolingDown,
        start: startCurrentResendCooldown,
    } = useOtpResendCooldown();
    const {
        secondsRemaining: newResendSeconds,
        isCoolingDown: isNewResendCoolingDown,
        start: startNewResendCooldown,
    } = useOtpResendCooldown();

    const authBaseUrl = `${getApiBaseUrl()}/auth`;
    const isEmail = contactType === 'email';
    const label = isEmail ? 'email' : 'phone number';
    const formattedCurrent = isEmail ? currentValue : formatPhoneLabel(currentValue);

    const callChangeContact = async (payload) => {
        const response = await fetch(`${authBaseUrl}/change-contact`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ contact_type: contactType, ...payload }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
            throw new Error(data?.detail || data?.error || 'Unable to update this contact detail.');
        }
        return data;
    };

    const sendCurrentCode = async () => {
        if (step === 'current_otp' && isCurrentResendCoolingDown) return;

        setBusy(true);
        setError(null);
        setNotice(null);
        try {
            await callChangeContact({ step: 'send_current_otp' });
            setStep('current_otp');
            setNotice(`Security code sent to ${formattedCurrent}.`);
            startCurrentResendCooldown();
        } catch (err) {
            setError(err.message || 'Unable to send the code.');
        } finally {
            setBusy(false);
        }
    };

    const verifyCurrentCode = async () => {
        const otp = currentOtp.replace(/\D/g, '').slice(0, OTP_LENGTH);
        if (otp.length !== OTP_LENGTH) {
            setError(`Please enter the ${OTP_LENGTH}-digit code.`);
            return;
        }
        setBusy(true);
        setError(null);
        try {
            const data = await callChangeContact({ step: 'verify_current_otp', otp });
            setChangeToken(data.change_token || '');
            setStep('new_value');
            setNotice(null);
        } catch (err) {
            setError(err.message || 'Invalid or expired code.');
        } finally {
            setBusy(false);
        }
    };

    const sendNewCode = async () => {
        if (step === 'new_otp' && isNewResendCoolingDown) return;

        let nextValue = '';
        if (isEmail) {
            const validationError = getEmailValidationError(newEmail);
            if (validationError) {
                setError(validationError);
                return;
            }
            nextValue = normalizeEmail(newEmail);
        } else {
            const digits = newPhone.replace(/\D/g, '');
            const dialCode = String(phoneCountry?.dialCode || '').replace(/\D/g, '');
            if (!digits || digits === dialCode) {
                setError('Please enter your phone number.');
                return;
            }
            if (digits.length < 8) {
                setError('Enter a valid phone number for the selected country.');
                return;
            }
            nextValue = digits;
        }

        setBusy(true);
        setError(null);
        setNotice(null);
        try {
            const data = await callChangeContact({
                step: 'send_new_otp',
                change_token: changeToken,
                new_value: nextValue,
            });
            setChangeToken(data.change_token || changeToken);
            setPendingNewValue(nextValue);
            setStep('new_otp');
            setNotice(`Verification code sent to ${isEmail ? nextValue : `+${nextValue}`}.`);
            startNewResendCooldown();
        } catch (err) {
            setError(err.message || `Unable to send the code to this ${label}.`);
        } finally {
            setBusy(false);
        }
    };

    const verifyNewCode = async () => {
        const otp = newOtp.replace(/\D/g, '').slice(0, OTP_LENGTH);
        if (otp.length !== OTP_LENGTH) {
            setError(`Please enter the ${OTP_LENGTH}-digit code.`);
            return;
        }
        setBusy(true);
        setError(null);
        try {
            await callChangeContact({
                step: 'verify_new_otp',
                change_token: changeToken,
                new_value: pendingNewValue,
                otp,
            });
            if (onSaved) await onSaved();
        } catch (err) {
            setError(err.message || 'Invalid or expired code.');
        } finally {
            setBusy(false);
        }
    };

    const inputClass = 'h-12 min-h-[48px] w-full shrink-0 card-wide:flex-1 rounded-xl border border-[#d9d9d9] bg-white px-4 text-[15px] font-bold text-[#414141] outline-none transition placeholder:font-medium placeholder:text-[#b0b0b0] focus:border-[#D81F26] focus:ring-4 focus:ring-[#D81F26]/10';
    const actionButtonClass = 'inline-flex h-12 w-full shrink-0 items-center justify-center rounded-xl bg-[#D81F26] px-6 text-sm font-black text-white transition hover:bg-[#B8181E] active:scale-[0.99] disabled:opacity-60 card-wide:w-auto';
    const currentResendLabel = currentResendSeconds > 0 ? `Resend code in ${currentResendSeconds}s` : 'Resend code';
    const newResendLabel = newResendSeconds > 0 ? `Resend code in ${newResendSeconds}s` : 'Resend code';

    return (
        <div className="border-t border-[#ececec] bg-[#f8f8f8] p-4">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <p className="text-[15px] font-black text-[#414141]">
                        {step === 'current_send' && `Confirm your current ${label}`}
                        {step === 'current_otp' && `Enter your security code`}
                        {step === 'new_value' && `Add your new ${label}`}
                        {step === 'new_otp' && `Confirm your new ${label}`}
                    </p>
                    <p className="mt-1 break-words text-[13px] font-medium leading-relaxed text-[#414141]">
                        {step === 'current_send' && `For your security, we will send a one-time code to ${formattedCurrent} before making changes.`}
                        {step === 'current_otp' && `Enter the one-time code sent to ${formattedCurrent}.`}
                        {step === 'new_value' && `Choose a ${label} you own. It must not already be linked to another CarFido account.`}
                        {step === 'new_otp' && `Enter the one-time code sent to ${isEmail ? pendingNewValue : `+${pendingNewValue}`}.`}
                    </p>
                </div>
                <button
                    type="button"
                    onClick={onCancel}
                    disabled={busy}
                    className="shrink-0 rounded-full border border-[#d9d9d9] bg-white px-3 py-1.5 text-xs font-black text-[#414141] transition hover:bg-[#F9F9F9] disabled:opacity-60"
                >
                    Cancel
                </button>
            </div>

            {step === 'current_send' && (
                <div className="mt-3 flex flex-col gap-2 card-wide:flex-row">
                    <button type="button" onClick={sendCurrentCode} disabled={busy} className={actionButtonClass}>
                        {busy ? 'Sending...' : 'Send Code'}
                    </button>
                </div>
            )}

            {step === 'current_otp' && (
                <div className="mt-3 flex flex-col gap-2 card-wide:flex-row">
                    <input
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={OTP_LENGTH}
                        value={currentOtp}
                        onChange={(e) => { setCurrentOtp(e.target.value.replace(/\D/g, '').slice(0, OTP_LENGTH)); if (error) setError(null); }}
                        onKeyDown={(e) => { if (e.key === 'Enter' && !busy) { e.preventDefault(); verifyCurrentCode(); } }}
                        placeholder="Enter 6-digit code"
                        className={`${inputClass} tracking-[0.25em] placeholder:tracking-normal`}
                    />
                    <button type="button" onClick={verifyCurrentCode} disabled={busy} className={actionButtonClass}>
                        {busy ? 'Verifying...' : 'Verify'}
                    </button>
                </div>
            )}

            {step === 'new_value' && (
                <div className="mt-3 flex flex-col gap-2 card-wide:flex-row">
                    {isEmail ? (
                        <input
                            type="email"
                            inputMode="email"
                            autoComplete="email"
                            autoCapitalize="none"
                            autoCorrect="off"
                            spellCheck={false}
                            value={newEmail}
                            onChange={(e) => { setNewEmail(e.target.value); if (error) setError(null); }}
                            onKeyDown={(e) => { if (e.key === 'Enter' && !busy) { e.preventDefault(); sendNewCode(); } }}
                            placeholder="new@example.com"
                            className={inputClass}
                        />
                    ) : (
                        <div className="min-w-0 flex-1">
                            <PhoneInput
                                country={'us'}
                                value={newPhone}
                                onChange={(value, country) => {
                                    setNewPhone(value);
                                    setPhoneCountry(country);
                                    if (error) setError(null);
                                }}
                                placeholder="908 242 3514"
                                enableSearch={true}
                                searchPlaceholder="Search country or code..."
                                disableSearchIcon={true}
                                disableCountryGuess={true}
                                disableInitialCountryGuess={false}
                                countryCodeEditable={false}
                                containerClass="dash-settings-phone"
                                dropdownClass="dash-settings-phone-dropdown"
                                inputProps={{
                                    onKeyDown: (e) => {
                                        if (e.key === 'Enter' && !busy) { e.preventDefault(); sendNewCode(); }
                                    },
                                }}
                            />
                        </div>
                    )}
                    <button type="button" onClick={sendNewCode} disabled={busy} className={actionButtonClass}>
                        {busy ? 'Sending...' : 'Send Code'}
                    </button>
                </div>
            )}

            {step === 'new_otp' && (
                <div className="mt-3 flex flex-col gap-2 card-wide:flex-row">
                    <input
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={OTP_LENGTH}
                        value={newOtp}
                        onChange={(e) => { setNewOtp(e.target.value.replace(/\D/g, '').slice(0, OTP_LENGTH)); if (error) setError(null); }}
                        onKeyDown={(e) => { if (e.key === 'Enter' && !busy) { e.preventDefault(); verifyNewCode(); } }}
                        placeholder="Enter 6-digit code"
                        className={`${inputClass} tracking-[0.25em] placeholder:tracking-normal`}
                    />
                    <button type="button" onClick={verifyNewCode} disabled={busy} className={actionButtonClass}>
                        {busy ? 'Saving...' : `Save ${isEmail ? 'Email' : 'Phone'}`}
                    </button>
                </div>
            )}

            {error && <p className="mt-2 text-xs font-bold text-red-600">{error}</p>}
            {!error && notice && <p className="mt-2 text-xs font-bold text-[#6B6B6B]">{notice}</p>}
            {step === 'current_otp' && (
                <button
                    type="button"
                    onClick={sendCurrentCode}
                    disabled={busy || isCurrentResendCoolingDown}
                    aria-label={currentResendLabel}
                    className="mt-2 text-xs font-black text-[#414141] transition hover:text-[#D81F26] disabled:opacity-60"
                >
                    {currentResendLabel}
                </button>
            )}
            {step === 'new_otp' && (
                <button
                    type="button"
                    onClick={sendNewCode}
                    disabled={busy || isNewResendCoolingDown}
                    aria-label={newResendLabel}
                    className="mt-2 text-xs font-black text-[#414141] transition hover:text-[#D81F26] disabled:opacity-60"
                >
                    {newResendLabel}
                </button>
            )}
        </div>
    );
};

// Attaches an email to an already-signed-in phone account, so the driver can
// later log in with either. Phone stays the account's primary identity.
const EmailAddressCard = ({ email, onSaved }) => {
    const [expanded, setExpanded] = useState(false);
    const [step, setStep] = useState('email'); // 'email' or 'otp'
    const [emailValue, setEmailValue] = useState('');
    const [otpValue, setOtpValue] = useState('');
    const [pendingEmail, setPendingEmail] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);
    const {
        secondsRemaining: resendSeconds,
        isCoolingDown: isResendCoolingDown,
        start: startResendCooldown,
        reset: resetResendCooldown,
    } = useOtpResendCooldown();
    // Non-null once the server reports the address belongs to another account and
    // offers to combine them. Holds the counts of what would move.
    const [mergePreview, setMergePreview] = useState(null);

    const savedEmail = normalizeEmail(email);
    const authBaseUrl = `${getApiBaseUrl()}/auth`;

    const resetPanel = () => {
        setStep('email');
        setEmailValue('');
        setOtpValue('');
        setPendingEmail('');
        setError(null);
        setNotice(null);
        setMergePreview(null);
        resetResendCooldown();
    };

    const handleToggle = () => {
        if (expanded) {
            setExpanded(false);
            resetPanel();
            return;
        }
        resetPanel();
        setExpanded(true);
    };

    const handleSendCode = async () => {
        if (step === 'otp' && isResendCoolingDown) return;

        const validationError = getEmailValidationError(emailValue);
        if (validationError) {
            setError(validationError);
            return;
        }

        setBusy(true);
        setError(null);
        setNotice(null);

        const targetEmail = normalizeEmail(emailValue);

        try {
            const response = await fetch(`${authBaseUrl}/add-email`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ email: targetEmail }),
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok) {
                throw new Error(getEmailAuthErrorMessage(response, data, 'Unable to send the code.'));
            }
            setPendingEmail(targetEmail);
            setStep('otp');
            setNotice(`Code sent to ${targetEmail}`);
            startResendCooldown();
        } catch (err) {
            setError(err.message || 'Unable to send the code.');
        } finally {
            setBusy(false);
        }
    };

    // confirmMerge=true replays the same verified code with the user's consent to
    // combine two accounts. The server answers 409 + merge_preview the first time so
    // we can say what is at stake before anything is touched.
    const handleVerifyCode = async (confirmMerge = false) => {
        const otp = otpValue.replace(/\D/g, '').slice(0, OTP_LENGTH);
        if (otp.length !== OTP_LENGTH) {
            setError(`Please enter the ${OTP_LENGTH}-digit code.`);
            return;
        }

        setBusy(true);
        setError(null);

        try {
            const response = await fetch(`${authBaseUrl}/verify-added-email`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ email: pendingEmail, otp, confirm_merge: confirmMerge }),
            });
            const data = await response.json().catch(() => ({}));
            if (response.status === 409 && data?.merge_available && !confirmMerge) {
                // Offer the merge instead of dead-ending on "already linked".
                setMergePreview(data.merge_preview || {});
                setError(null);
                return;
            }
            if (!response.ok) {
                throw new Error(getEmailAuthErrorMessage(response, data, 'Invalid or expired code.'));
            }
            setExpanded(false);
            resetPanel();
            if (onSaved) onSaved();
        } catch (err) {
            setError(err.message || 'Invalid or expired code.');
        } finally {
            setBusy(false);
        }
    };

    const inputClass = 'h-12 min-h-[48px] w-full shrink-0 card-wide:flex-1 rounded-xl border border-[#d9d9d9] bg-white px-4 text-[15px] font-bold text-[#414141] outline-none transition placeholder:font-medium placeholder:text-[#b0b0b0] focus:border-[#D81F26] focus:ring-4 focus:ring-[#D81F26]/10';
    const actionButtonClass = 'inline-flex h-12 w-full shrink-0 items-center justify-center rounded-xl bg-[#D81F26] px-6 text-sm font-black text-white transition hover:bg-[#B8181E] active:scale-[0.99] disabled:opacity-60 card-wide:w-auto';
    const resendLabel = resendSeconds > 0 ? `Resend code in ${resendSeconds}s` : 'Resend code';

    // One card, not two — the header row and the drawer share a border so the
    // open state reads as a single control instead of two stacked panels.
    return (
        <div data-tooltip-boundary className="mt-5 overflow-hidden rounded-2xl border border-[#ececec] bg-white">
            <div className="flex flex-col gap-3 p-4 card-wide:flex-row card-wide:items-center card-wide:justify-between card-wide:gap-4">
                <div className="flex min-w-0 flex-1 items-start gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#F9F9F9] text-[#414141] card-wide:h-11 card-wide:w-11 card-wide:rounded-2xl">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="2.5" y="4.5" width="19" height="15" rx="3" />
                            <path d="m3.5 7 8.5 6 8.5-6" />
                        </svg>
                    </div>
                    <div className="min-w-0">
                        <div className="flex items-center gap-1">
                            <p className="text-[15px] font-black text-[#414141]">Email address</p>
                            <InfoTooltip label="About email address" text="Use this email for sign-in, account updates, and recovery." />
                        </div>
                        <p className="mt-0.5 text-sm font-bold text-[#6B6B6B] [overflow-wrap:anywhere]">
                            {savedEmail || 'No email added'}
                        </p>
                    </div>
                </div>

                {savedEmail ? (
                    <div className="flex shrink-0 items-center gap-2 pl-[52px] card-wide:pl-0">
                        <span className="inline-flex min-h-[34px] items-center justify-center gap-1.5 rounded-full bg-emerald-50 px-3 text-xs font-black text-emerald-700">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M20 6 9 17l-5-5" />
                            </svg>
                            Verified
                        </span>
                        <button
                            type="button"
                            onClick={handleToggle}
                            aria-expanded={expanded}
                            className="inline-flex min-h-[34px] items-center justify-center rounded-full border border-[#d9d9d9] bg-white px-4 text-xs font-black text-[#414141] transition hover:border-[#D81F26] hover:text-[#D81F26] active:scale-[0.99]"
                        >
                            {expanded ? 'Cancel' : 'Edit'}
                        </button>
                    </div>
                ) : (
                    <button
                        type="button"
                        onClick={handleToggle}
                        aria-expanded={expanded}
                        className="ml-[52px] inline-flex min-h-[34px] shrink-0 items-center justify-center self-start rounded-full border border-[#d9d9d9] card-wide:ml-0 card-wide:self-auto bg-white px-4 text-xs font-black text-[#414141] transition hover:border-[#D81F26] hover:text-[#D81F26] active:scale-[0.99]"
                    >
                        {expanded ? 'Cancel' : 'Add Email'}
                    </button>
                )}
            </div>

            <div className={`dash-collapsible ${expanded ? 'is-open' : ''}`}>
                <div>
                    {savedEmail ? (
                        <ChangeContactEditor
                            contactType="email"
                            currentValue={savedEmail}
                            onCancel={() => { setExpanded(false); resetPanel(); }}
                            onSaved={async () => {
                                setExpanded(false);
                                resetPanel();
                                if (onSaved) await onSaved();
                            }}
                        />
                    ) : (
                    <div className="border-t border-[#ececec] bg-[#f8f8f8] p-4">
                        <p className="text-[15px] font-black text-[#414141]">
                            {step === 'otp' ? 'Enter the code' : 'Add your email'}
                        </p>
                        <p className="mt-1 break-words text-[13px] font-medium leading-relaxed text-[#414141]">
                            {step === 'otp'
                                ? `We sent a one-time code to ${pendingEmail}.`
                                : "We'll send you a one-time code to verify your email address."}
                        </p>

                        {step === 'email' ? (
                            <div className="mt-3 flex flex-col gap-2 card-wide:flex-row">
                                <input
                                    type="email"
                                    inputMode="email"
                                    autoComplete="email"
                                    autoCapitalize="none"
                                    autoCorrect="off"
                                    spellCheck={false}
                                    value={emailValue}
                                    onChange={(e) => { setEmailValue(e.target.value); if (error) setError(null); }}
                                    onKeyDown={(e) => { if (e.key === 'Enter' && !busy) { e.preventDefault(); handleSendCode(); } }}
                                    placeholder="you@example.com"
                                    className={inputClass}
                                />
                                <button type="button" onClick={handleSendCode} disabled={busy} className={actionButtonClass}>
                                    {busy ? 'Sending...' : 'Send Code'}
                                </button>
                            </div>
                        ) : (
                            <div className="mt-3 flex flex-col gap-2 card-wide:flex-row">
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    autoComplete="one-time-code"
                                    maxLength={OTP_LENGTH}
                                    value={otpValue}
                                    onChange={(e) => { setOtpValue(e.target.value.replace(/\D/g, '').slice(0, OTP_LENGTH)); if (error) setError(null); }}
                                    onKeyDown={(e) => { if (e.key === 'Enter' && !busy) { e.preventDefault(); handleVerifyCode(); } }}
                                    placeholder="Enter 6-digit code"
                                    className={`${inputClass} tracking-[0.25em] placeholder:tracking-normal`}
                                />
                                <button type="button" onClick={() => handleVerifyCode()} disabled={busy} className={actionButtonClass}>
                                    {busy ? 'Verifying...' : 'Verify'}
                                </button>
                            </div>
                        )}

                        {mergePreview && (
                            <MergeConfirmPrompt
                                preview={mergePreview}
                                busy={busy}
                                what="email"
                                onConfirm={() => handleVerifyCode(true)}
                                onCancel={() => { setMergePreview(null); setStep('email'); setOtpValue(''); }}
                            />
                        )}

                        {error && <p className="mt-2 text-xs font-bold text-red-600">{error}</p>}
                        {!error && !mergePreview && notice && step === 'otp' && (
                            <p className="mt-2 text-xs font-bold text-[#6B6B6B]">{notice}</p>
                        )}

                        {step === 'otp' && !mergePreview && (
                            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-2">
                                <button
                                    type="button"
                                    onClick={handleSendCode}
                                    disabled={busy || isResendCoolingDown}
                                    aria-label={resendLabel}
                                    className="text-xs font-black text-[#D81F26] transition hover:text-[#B8181E] disabled:text-[#b0b0b0]"
                                >
                                    {resendLabel}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => { setStep('email'); setOtpValue(''); setError(null); setNotice(null); resetResendCooldown(); }}
                                    className="text-xs font-black text-[#414141] transition hover:text-[#D81F26]"
                                >
                                    Use a different email
                                </button>
                            </div>
                        )}

                        <p className="mt-3 flex items-center gap-1.5 text-xs font-medium text-[#414141]">
                            <svg className="shrink-0" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="4" y="11" width="16" height="10" rx="2" />
                                <path d="M8 11V7a4 4 0 0 1 8 0v4" />
                            </svg>
                            Your email stays private and is used only for account access and service updates.
                        </p>
                    </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// The mirror of EmailAddressCard, for accounts created with an email and no phone.
// Sends the code through the existing /auth/request-otp, then links it with
// /auth/verify-added-phone — NOT /auth/verify-otp, which would sign the user in as
// that number and drop them into a different account.
const PhoneNumberCard = ({ phone, onSaved }) => {
    const [expanded, setExpanded] = useState(false);
    const [step, setStep] = useState('phone'); // 'phone' or 'otp'
    const [phoneValue, setPhoneValue] = useState('');
    // Country object from the picker — carried so the display and the "you typed
    // nothing but the dial code" check both know which country is selected.
    const [phoneCountry, setPhoneCountry] = useState(null);
    const [otpValue, setOtpValue] = useState('');
    const [pendingPhone, setPendingPhone] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);
    const {
        secondsRemaining: resendSeconds,
        isCoolingDown: isResendCoolingDown,
        start: startResendCooldown,
        reset: resetResendCooldown,
    } = useOtpResendCooldown();
    const [mergePreview, setMergePreview] = useState(null);

    const savedPhone = String(phone || '').trim();
    const authBaseUrl = `${getApiBaseUrl()}/auth`;

    const resetPanel = () => {
        setStep('phone');
        setPhoneValue('');
        setPhoneCountry(null);
        setOtpValue('');
        setPendingPhone('');
        setError(null);
        setNotice(null);
        setMergePreview(null);
        resetResendCooldown();
    };

    const handleToggle = () => {
        if (expanded) {
            setExpanded(false);
            resetPanel();
            return;
        }
        // Deliberately NOT prefilled with the saved number: PhoneInput would apply the
        // default country's formatting mask to it (a +91 number rendered as US came out
        // as "+9 (170) 755-5924"). The picker is only ever shown when there is no number
        // to prefill anyway.
        resetPanel();
        setExpanded(true);
    };

    const handleSendCode = async () => {
        if (step === 'otp' && isResendCoolingDown) return;

        const digits = phoneValue.replace(/\D/g, '');
        // PhoneInput always prefixes the dial code, so a "number" of only the dial
        // code means the user picked a country but typed nothing.
        const dialCode = String(phoneCountry?.dialCode || '').replace(/\D/g, '');
        if (!digits || digits === dialCode) {
            setError('Please enter your phone number.');
            return;
        }
        if (digits.length < 8) {
            setError('Enter a valid phone number for the selected country.');
            return;
        }

        setBusy(true);
        setError(null);
        setNotice(null);

        try {
            const response = await fetch(`${authBaseUrl}/request-otp`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ phone: digits }),
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok) {
                throw new Error(data?.detail || data?.error || 'Unable to send the code.');
            }
            setPendingPhone(digits);
            setStep('otp');
            setNotice(`Code sent to +${digits}`);
            startResendCooldown();
        } catch (err) {
            setError(err.message || 'Unable to send the code.');
        } finally {
            setBusy(false);
        }
    };

    // See the email card: confirmMerge=true replays the verified code with consent to
    // combine two accounts, after the server has told us what would move.
    const handleVerifyCode = async (confirmMerge = false) => {
        const otp = otpValue.replace(/\D/g, '').slice(0, OTP_LENGTH);
        if (otp.length !== OTP_LENGTH) {
            setError(`Please enter the ${OTP_LENGTH}-digit code.`);
            return;
        }

        setBusy(true);
        setError(null);

        try {
            const response = await fetch(`${authBaseUrl}/verify-added-phone`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ phone: pendingPhone, otp, confirm_merge: confirmMerge }),
            });
            const data = await response.json().catch(() => ({}));
            if (response.status === 409 && data?.merge_available && !confirmMerge) {
                setMergePreview(data.merge_preview || {});
                setError(null);
                return;
            }
            if (!response.ok) {
                throw new Error(data?.detail || data?.error || 'Invalid or expired code.');
            }
            setExpanded(false);
            resetPanel();
            if (onSaved) onSaved();
        } catch (err) {
            setError(err.message || 'Invalid or expired code.');
        } finally {
            setBusy(false);
        }
    };

    const inputClass = 'h-12 min-h-[48px] w-full shrink-0 card-wide:flex-1 rounded-xl border border-[#d9d9d9] bg-white px-4 text-[15px] font-bold text-[#414141] outline-none transition placeholder:font-medium placeholder:text-[#b0b0b0] focus:border-[#D81F26] focus:ring-4 focus:ring-[#D81F26]/10';
    const actionButtonClass = 'inline-flex h-12 w-full shrink-0 items-center justify-center rounded-xl bg-[#D81F26] px-6 text-sm font-black text-white transition hover:bg-[#B8181E] active:scale-[0.99] disabled:opacity-60 card-wide:w-auto';
    const resendLabel = resendSeconds > 0 ? `Resend code in ${resendSeconds}s` : 'Resend code';

    return (
        // No overflow-hidden here (unlike the email card): the country dropdown is
        // absolutely positioned and would be clipped by it.
        <div data-tooltip-boundary className="dash-phone-card mt-3 rounded-2xl border border-[#ececec] bg-white">
            {/* react-phone-input-2 ships its own look; these overrides make it match the
                card's other inputs (same height, radius and focus ring). Scoped to the
                dash-settings-phone classes so the login modal's styling is untouched. */}
            <style>{`
                /* .dash-collapsible > * sets overflow:hidden so the open/close height
                   animation clips cleanly. That also clips the country dropdown, so
                   release it once the panel is fully open — delayed to the end of the
                   280ms transition so the animation itself still clips. */
                .dash-phone-card .dash-collapsible.is-open > * {
                    overflow: visible;
                    transition: visibility 0s, overflow 0s linear 280ms;
                }
                .dash-settings-phone { width: 100% !important; }
                .dash-settings-phone .form-control {
                    width: 100% !important;
                    height: 48px !important;
                    border-radius: 12px !important;
                    border: 1px solid #d9d9d9 !important;
                    background: #ffffff !important;
                    font-size: 15px !important;
                    font-weight: 700 !important;
                    color: #414141 !important;
                    padding-left: 58px !important;
                }
                .dash-settings-phone .form-control:focus {
                    border-color: #D81F26 !important;
                    box-shadow: 0 0 0 4px rgba(216,31,38,0.10) !important;
                }
                .dash-settings-phone .flag-dropdown {
                    background: transparent !important;
                    border: none !important;
                    border-right: 1px solid #ececec !important;
                    border-radius: 12px 0 0 12px !important;
                }
                .dash-settings-phone .selected-flag { padding-left: 12px !important; width: 50px !important; }
                .dash-settings-phone .selected-flag:hover,
                .dash-settings-phone .selected-flag:focus { background: #f8f8f8 !important; }
                .dash-settings-phone .selected-flag .arrow { border-top-color: #8C8C8C !important; }
                .dash-settings-phone .selected-flag .arrow.up {
                    border-bottom-color: #8C8C8C !important;
                    border-top-color: transparent !important;
                }
                .dash-settings-phone-dropdown {
                    border-radius: 14px !important;
                    border: 1px solid rgba(0,0,0,0.08) !important;
                    box-shadow: 0 18px 40px rgba(0,0,0,0.12) !important;
                    padding: 8px !important;
                    margin-top: 4px !important;
                    max-height: 240px !important;
                    z-index: 9999 !important;
                    /* Anchor to the input and stay inside the card on narrow screens. */
                    width: 320px !important;
                    max-width: calc(100vw - 48px) !important;
                    left: 0 !important;
                    overflow-x: hidden !important;
                }
                .dash-settings-phone-dropdown .country {
                    border-radius: 10px !important;
                    padding: 8px 12px !important;
                    font-size: 14px !important;
                    font-weight: 600 !important;
                    color: #414141 !important;
                }
                .dash-settings-phone-dropdown .country .dial-code {
                    color: #8C8C8C !important;
                    font-weight: 800 !important;
                }
                .dash-settings-phone-dropdown .country:hover { background-color: #f6f6f6 !important; }
                .dash-settings-phone-dropdown .country.highlight { background-color: #ededed !important; }
                .dash-settings-phone-dropdown .search {
                    padding: 8px !important;
                    margin: -8px -8px 6px !important;
                    background: #ffffff !important;
                }
                .dash-settings-phone-dropdown .search-box {
                    width: 100% !important;
                    margin: 0 !important;
                    height: 38px !important;
                    border-radius: 10px !important;
                    border: 1px solid #d9d9d9 !important;
                    font-size: 14px !important;
                    padding: 0 12px !important;
                }
            `}</style>
            <div className="flex flex-col gap-3 p-4 card-wide:flex-row card-wide:items-center card-wide:justify-between card-wide:gap-4">
                <div className="flex min-w-0 flex-1 items-start gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#F9F9F9] text-[#414141] card-wide:h-11 card-wide:w-11 card-wide:rounded-2xl">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
                        </svg>
                    </div>
                    <div className="min-w-0">
                        <div className="flex items-center gap-1">
                            <p className="text-[15px] font-black text-[#414141]">Phone number</p>
                            <InfoTooltip label="About phone number" text="Use this number for sign-in, verification, and account recovery." />
                        </div>
                        <p className="mt-0.5 truncate text-sm font-bold text-[#6B6B6B]">
                            {savedPhone ? formatPhoneLabel(savedPhone) : 'No phone added'}
                        </p>
                    </div>
                </div>

                {savedPhone ? (
                    <div className="flex shrink-0 items-center gap-2 pl-[52px] card-wide:pl-0">
                        <span className="inline-flex min-h-[34px] items-center justify-center gap-1.5 rounded-full bg-emerald-50 px-3 text-xs font-black text-emerald-700">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M20 6 9 17l-5-5" />
                            </svg>
                            Verified
                        </span>
                        <button
                            type="button"
                            onClick={handleToggle}
                            aria-expanded={expanded}
                            className="inline-flex min-h-[34px] items-center justify-center rounded-full border border-[#d9d9d9] bg-white px-4 text-xs font-black text-[#414141] transition hover:border-[#D81F26] hover:text-[#D81F26] active:scale-[0.99]"
                        >
                            {expanded ? 'Cancel' : 'Edit'}
                        </button>
                    </div>
                ) : (
                    <button
                        type="button"
                        onClick={handleToggle}
                        aria-expanded={expanded}
                        className="ml-[52px] inline-flex min-h-[34px] shrink-0 items-center justify-center self-start rounded-full border border-[#d9d9d9] card-wide:ml-0 card-wide:self-auto bg-white px-4 text-xs font-black text-[#414141] transition hover:border-[#D81F26] hover:text-[#D81F26] active:scale-[0.99]"
                    >
                        {expanded ? 'Cancel' : 'Add Phone'}
                    </button>
                )}
            </div>

            <div className={`dash-collapsible ${expanded ? 'is-open' : ''}`}>
                <div>
                    {savedPhone ? (
                        <ChangeContactEditor
                            contactType="phone"
                            currentValue={savedPhone}
                            onCancel={() => { setExpanded(false); resetPanel(); }}
                            onSaved={async () => {
                                setExpanded(false);
                                resetPanel();
                                if (onSaved) await onSaved();
                            }}
                        />
                    ) : (
                    <div className="border-t border-[#ececec] bg-[#f8f8f8] p-4">
                        <p className="text-[15px] font-black text-[#414141]">
                            {step === 'otp' ? 'Enter the code' : 'Add your phone number'}
                        </p>
                        <p className="mt-1 break-words text-[13px] font-medium leading-relaxed text-[#414141]">
                            {step === 'otp'
                                ? `We sent a one-time code to +${pendingPhone}.`
                                : "Choose your country and enter your number. We'll send a one-time verification code."}
                        </p>

                        {step === 'phone' ? (
                            <div className="mt-3 flex flex-col gap-2 card-wide:flex-row">
                                {/* Same picker as the login modal — the country code is
                                    chosen from a list rather than typed, so the user
                                    cannot guess it wrong or omit it. */}
                                <div className="min-w-0 flex-1">
                                    <PhoneInput
                                        country={'us'}
                                        value={phoneValue}
                                        onChange={(value, country) => {
                                            setPhoneValue(value);
                                            setPhoneCountry(country);
                                            if (error) setError(null);
                                        }}
                                        placeholder="908 242 3514"
                                        enableSearch={true}
                                        searchPlaceholder="Search country or code…"
                                        disableSearchIcon={true}
                                        disableCountryGuess={true}
                                        disableInitialCountryGuess={false}
                                        countryCodeEditable={false}
                                        containerClass="dash-settings-phone"
                                        dropdownClass="dash-settings-phone-dropdown"
                                        inputProps={{
                                            onKeyDown: (e) => {
                                                if (e.key === 'Enter' && !busy) { e.preventDefault(); handleSendCode(); }
                                            },
                                        }}
                                    />
                                </div>
                                <button type="button" onClick={handleSendCode} disabled={busy} className={actionButtonClass}>
                                    {busy ? 'Sending...' : 'Send Code'}
                                </button>
                            </div>
                        ) : (
                            <div className="mt-3 flex flex-col gap-2 card-wide:flex-row">
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    autoComplete="one-time-code"
                                    maxLength={OTP_LENGTH}
                                    value={otpValue}
                                    onChange={(e) => { setOtpValue(e.target.value.replace(/\D/g, '').slice(0, OTP_LENGTH)); if (error) setError(null); }}
                                    onKeyDown={(e) => { if (e.key === 'Enter' && !busy) { e.preventDefault(); handleVerifyCode(); } }}
                                    placeholder="Enter 6-digit code"
                                    className={`${inputClass} tracking-[0.25em] placeholder:tracking-normal`}
                                />
                                <button type="button" onClick={() => handleVerifyCode()} disabled={busy} className={actionButtonClass}>
                                    {busy ? 'Verifying...' : 'Verify'}
                                </button>
                            </div>
                        )}

                        {mergePreview && (
                            <MergeConfirmPrompt
                                preview={mergePreview}
                                busy={busy}
                                what="number"
                                onConfirm={() => handleVerifyCode(true)}
                                onCancel={() => { setMergePreview(null); setStep('phone'); setOtpValue(''); }}
                            />
                        )}

                        {error && <p className="mt-2 text-xs font-bold text-red-600">{error}</p>}
                        {!error && !mergePreview && notice && step === 'otp' && (
                            <p className="mt-2 text-xs font-bold text-[#6B6B6B]">{notice}</p>
                        )}

                        {step === 'otp' && !mergePreview && (
                            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-2">
                                <button
                                    type="button"
                                    onClick={handleSendCode}
                                    disabled={busy || isResendCoolingDown}
                                    aria-label={resendLabel}
                                    className="text-xs font-black text-[#D81F26] transition hover:text-[#B8181E] disabled:text-[#b0b0b0]"
                                >
                                    {resendLabel}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => { setStep('phone'); setOtpValue(''); setError(null); setNotice(null); resetResendCooldown(); }}
                                    className="text-xs font-black text-[#414141] transition hover:text-[#D81F26]"
                                >
                                    Use a different number
                                </button>
                            </div>
                        )}

                        <p className="mt-3 flex items-center gap-1.5 text-xs font-medium text-[#414141]">
                            <svg className="shrink-0" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="4" y="11" width="16" height="10" rx="2" />
                                <path d="M8 11V7a4 4 0 0 1 8 0v4" />
                            </svg>
                            Your number stays private and is used only for account access and service updates.
                        </p>
                    </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export const MarketingPreferencesCard = ({ identity, onSessionExpired }) => {
    const [optIn, setOptIn] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [reload, setReload] = useState(0);
    const endpoint = `${getApiBaseUrl()}/auth/marketing-preference`;

    useEffect(() => {
        const controller = new AbortController();
        setOptIn(null);
        setError('');
        const load = async () => {
            try {
                const response = await fetch(endpoint, { credentials: 'include', signal: controller.signal });
                const data = await response.json().catch(() => ({}));
                if (controller.signal.aborted) return;
                if (response.status === 401) onSessionExpired?.();
                if (!response.ok || typeof data.marketing_opt_in !== 'boolean') {
                    throw new Error('Could not load your marketing preference. Please try again.');
                }
                setOptIn(data.marketing_opt_in);
            } catch (err) {
                if (!controller.signal.aborted) setError(err.message || 'Could not load your marketing preference.');
            }
        };
        load();
        return () => controller.abort();
    }, [endpoint, identity, onSessionExpired, reload]);

    const save = async (next) => {
        if (saving || optIn === null) return;
        setSaving(true);
        setError('');
        setNotice('');
        try {
            const response = await fetch(endpoint, {
                method: 'PUT', credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ marketing_opt_in: next }),
            });
            const data = await response.json().catch(() => ({}));
            if (response.status === 401) onSessionExpired?.();
            if (!response.ok || typeof data.marketing_opt_in !== 'boolean') {
                throw new Error('Could not save your marketing preference. Please try again.');
            }
            setOptIn(data.marketing_opt_in);
            setNotice('Preference saved.');
        } catch (err) {
            setError(err.message || 'Could not save your marketing preference.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <section aria-busy={saving} className="rounded-2xl border border-border-control/30 bg-surface p-5 font-brand text-fg shadow-sm">
            <h3 className="mb-3 font-brand text-lg font-extrabold">Marketing preferences</h3>
            <label className="flex cursor-pointer items-start gap-3 text-sm font-medium leading-relaxed">
                <input
                    type="checkbox"
                    checked={optIn === true}
                    disabled={optIn === null || saving}
                    onChange={(event) => save(event.target.checked)}
                    className="mt-0.5 h-[22px] w-[22px] shrink-0 accent-action focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 disabled:cursor-wait"
                />
                <span>{MARKETING_CONSENT_TEXT}</span>
            </label>
            {optIn === null && !error && <p role="status" className="mt-2 text-sm text-fg-muted">Loading your preference...</p>}
            {saving && <p role="status" className="mt-2 text-sm text-fg-muted">Saving...</p>}
            {error && <p role="alert" className="mt-2 text-sm text-danger">{error}</p>}
            {optIn === null && error && (
                <button type="button" onClick={() => setReload((value) => value + 1)} className="mt-2 rounded-sm font-bold text-link underline focus-visible:ring-2 focus-visible:ring-focus">Try again</button>
            )}
            {notice && <p role="status" className="mt-2 text-sm text-fg-muted">{notice}</p>}
        </section>
    );
};

const SettingsPage = () => {
    const navigate = useNavigate();
    const { authState, openAuthModal, onLogout, displayName = '', refreshDisplayName, refreshAuthState, avatarUrl = '', setAvatarUrl } = useOutletContext();
    const handleAvatarChange = useCallback((nextUrl) => {
        if (setAvatarUrl) setAvatarUrl(nextUrl);
    }, [setAvatarUrl]);
    const photoEditor = useProfilePhotoEditor({ avatarUrl, onAvatarChange: handleAvatarChange });
    const [documents, setDocuments] = useState([]);
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [profileLoading, setProfileLoading] = useState(false);
    const [error, setError] = useState(null);
    const [eraseConfirm, setEraseConfirm] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deletePhrase, setDeletePhrase] = useState('');
    const [deleteBusy, setDeleteBusy] = useState(false);
    const [deleteError, setDeleteError] = useState(null);

    const isAuthenticated = authState?.status === 'authenticated';
    const phone = authState?.phone || '';
    const email = authState?.email || '';
    // Backend identity: the phone when the account has one, otherwise the email.
    // user_details resolves either, so every lookup below works for email-only users
    // instead of bailing out on the missing phone.
    const identity = phone || email;
    const webUserId = localStorage.getItem('dash_web_user_id') || '';
    const testerMode = localStorage.getItem('TesterMode') || null;
    const [savedProfileName, setSavedProfileName] = useState('');
    const profileName = savedProfileName || profile?.username || displayName || '';
    const accountLabel = profileName || formatAccountLabel(phone, email);
    // Sub-label under the account name: show whichever identifier is NOT already the
    // headline, so an email user does not see their address printed twice. The phone
    // is never repeated under the name: the Phone number card below already shows it.
    const accountSubLabel = profileName
        ? (formatPhoneLabel(phone) ? '' : formatAccountLabel(phone, email))
        : (formatPhoneLabel(phone) ? email : '');
    const activeVehicle = profile?.active_vehicle || null;
    const vehicleCount = profile?.vehicle_count ?? (Array.isArray(profile?.vehicles) ? profile.vehicles.length : 0);
    const planLabel = 'Standard Plan';
    const canConfirmAccountDelete = deletePhrase === DELETE_CONFIRMATION_PHRASE;

    const handleErase = async () => {
        if (!eraseConfirm) {
            setEraseConfirm(true);
            return;
        }
        eraseAllSessionData();
        notifyChatReset('local-data-erased', { persist: false });
        // Await logout before reloading so the server session is actually revoked
        // before the fresh page calls /me — otherwise the reload could re-detect a
        // still-valid session and appear signed-in again.
        if (isAuthenticated) await onLogout();
        // Hard reload so no stale in-memory state survives (see delete handler).
        window.location.replace('/');
    };

    const openDeleteDialog = () => {
        if (!isAuthenticated) {
            openAuthModal();
            return;
        }
        setDeletePhrase('');
        setDeleteError(null);
        setDeleteDialogOpen(true);
    };

    const closeDeleteDialog = useCallback(() => {
        if (deleteBusy) return;
        setDeleteDialogOpen(false);
        setDeletePhrase('');
        setDeleteError(null);
    }, [deleteBusy]);

    const handleDeleteAccountData = async () => {
        if (!isAuthenticated) {
            openAuthModal();
            return;
        }
        if (!canConfirmAccountDelete) {
            setDeleteError(`Type "${DELETE_CONFIRMATION_PHRASE}" to confirm deletion.`);
            return;
        }
        setDeleteBusy(true);
        setDeleteError(null);
        try {
            const response = await fetch(`${getApiBaseUrl()}/api/account/delete`, {
                method: 'DELETE',
                credentials: 'include',
            });
            const payload = await response.json().catch(() => ({}));
            if (!response.ok) {
                throw new Error(payload.message || payload.detail || 'Unable to delete account data.');
            }
            eraseAllSessionData();
            notifyChatReset('account-deleted', { persist: false });
            if (isAuthenticated) await onLogout();
            // Hard reload (not a soft navigate) so all in-memory React state — chat
            // messages, garage, auth — is dropped and the app re-initialises as an
            // anonymous visitor. A client-side navigate would leave the deleted
            // account's data on screen until the user manually refreshed.
            window.location.replace('/');
        } catch (err) {
            setDeleteError(err.message || 'Unable to delete account data.');
        } finally {
            setDeleteBusy(false);
        }
    };

    const fetchDocuments = useCallback(async () => {
        if (!isAuthenticated || !identity) return;
        setLoading(true);
        setError(null);

        try {
            const response = await fetch(`${getUserDetailsBaseUrl()}/api/documents/me`, {
                credentials: 'include',
            });
            if (!response.ok) {
                const text = await response.text();
                throw new Error(text || 'Failed to load saved data.');
            }
            const data = await response.json();
            setDocuments(data.documents || []);
        } catch (err) {
            setError(err.message || 'Unable to load saved data.');
        } finally {
            setLoading(false);
        }
    }, [identity, isAuthenticated]);

    const fetchProfile = useCallback(async () => {
        if (!isAuthenticated || !identity) {
            setProfile(null);
            return;
        }
        setProfileLoading(true);
        try {
            const lookupUrl = `${getUserDetailsBaseUrl()}/api/lookup_user_data?phone_number=${encodeURIComponent(identity)}`;
            const response = await fetch(`${lookupUrl}&query=vehicle_worklist`);
            if (!response.ok) throw new Error('Failed to load profile.');
            const data = await response.json();
            let result = data?.result && typeof data.result === 'object' ? data.result : null;
            if (!result) {
                // The vehicle query answers with a sentence, not the profile, when the
                // account has no vehicles yet — a brand-new user straight out of the
                // profile step. The plain lookup still returns name, region and contacts.
                const profileResponse = await fetch(lookupUrl);
                if (profileResponse.ok) {
                    const profileData = await profileResponse.json();
                    result = profileData?.result && typeof profileData.result === 'object' ? profileData.result : null;
                }
            }
            setProfile(result);
            if (refreshDisplayName) refreshDisplayName();
        } catch (err) {
            console.error('Failed to load settings profile:', err);
            setProfile(null);
        } finally {
            setProfileLoading(false);
        }
    }, [identity, isAuthenticated, refreshDisplayName]);

    useEffect(() => {
        fetchDocuments();
    }, [fetchDocuments]);

    const handleNameSaved = useCallback(async (nextName) => {
        setSavedProfileName(nextName);
        await fetchProfile();
    }, [fetchProfile]);
    const nameEditor = useProfileNameEditor({ identity, profileName, onSaved: handleNameSaved });

    useEffect(() => {
        if (savedProfileName && profile?.username === savedProfileName) setSavedProfileName('');
    }, [profile?.username, savedProfileName]);

    useEffect(() => {
        fetchProfile();
        window.addEventListener('dash_vehicle_updated', fetchProfile);
        window.addEventListener('dash_vehicle_switched', fetchProfile);
        return () => {
            window.removeEventListener('dash_vehicle_updated', fetchProfile);
            window.removeEventListener('dash_vehicle_switched', fetchProfile);
        };
    }, [fetchProfile]);

    useEffect(() => {
        if (!deleteDialogOpen) return undefined;
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') closeDeleteDialog();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [deleteDialogOpen, closeDeleteDialog]);

    const uploadsCount = documents.filter((doc) => doc.source !== 'agent_generated').length;
    const reportsCount = documents.filter((doc) => doc.source === 'agent_generated').length;

    return (
        <div className="h-full w-full overflow-y-auto bg-[#F9F9F9] custom-scrollbar relative">
            <div className="mx-auto flex min-h-full w-full max-w-3xl flex-col px-4 pb-12 pt-8 md:px-6 md:pt-10">
                <header className="mb-5">
                    <h1 className="text-3xl font-black tracking-tight text-[#414141] md:text-[34px]">Settings</h1>
                    <p className="mt-2 max-w-xl text-[15px] font-medium leading-relaxed text-[#414141]">
                        Keep your profile, sign-in methods, and CarFido preferences up to date.
                    </p>
                </header>

                {eraseConfirm && (
                    <div
                        className="fixed inset-0 z-10"
                        onClick={() => {
                            setEraseConfirm(false);
                        }}
                    />
                )}

                {deleteDialogOpen && (
                    <div className="animate-dash-overlay-in fixed inset-0 z-[120] flex items-center justify-center overflow-y-auto bg-black/45 px-4 py-6 backdrop-blur-sm">
                        <div
                            className="absolute inset-0"
                            onMouseDown={closeDeleteDialog}
                            aria-hidden="true"
                        />
                        <form
                            role="dialog"
                            aria-modal="true"
                            aria-labelledby="delete-account-dialog-title"
                            aria-describedby="delete-account-dialog-description"
                            className="animate-dash-modal-in relative z-[121] w-full max-w-md overflow-hidden rounded-[26px] border border-red-100 bg-white shadow-[0_30px_90px_rgba(127,29,29,0.28)]"
                            onSubmit={(event) => {
                                event.preventDefault();
                                handleDeleteAccountData();
                            }}
                        >
                            <div className="border-b border-red-100 bg-red-50 px-5 py-4">
                                <div className="flex items-start justify-between gap-4">
                                    <div>
                                        <p className="text-[10px] font-black uppercase tracking-[0.18em] text-red-600">Permanent deletion</p>
                                        <h2 id="delete-account-dialog-title" className="mt-1 text-xl font-black text-[#414141]">
                                            Delete account data?
                                        </h2>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={closeDeleteDialog}
                                        disabled={deleteBusy}
                                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-red-100 bg-white text-red-400 transition hover:text-red-700 disabled:opacity-60"
                                        aria-label="Close delete confirmation"
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                                            <line x1="18" y1="6" x2="6" y2="18" />
                                            <line x1="6" y1="6" x2="18" y2="18" />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                            <div className="px-5 py-5">
                                <p id="delete-account-dialog-description" className="text-sm font-semibold leading-relaxed text-[#414141]">
                                    This removes saved vehicles, chats, diagnostics, reports, uploaded files, auth sessions, and browser keys. To continue, type the phrase below.
                                </p>
                                <p className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-center font-mono text-sm font-black text-red-600">
                                    {DELETE_CONFIRMATION_PHRASE}
                                </p>
                                <label htmlFor="delete-confirmation-phrase" className="mt-4 block text-xs font-black uppercase tracking-[0.16em] text-[#414141]">
                                    Confirmation phrase
                                </label>
                                <input
                                    id="delete-confirmation-phrase"
                                    type="text"
                                    value={deletePhrase}
                                    onChange={(event) => setDeletePhrase(event.target.value)}
                                    disabled={deleteBusy}
                                    autoFocus
                                    autoComplete="off"
                                    spellCheck={false}
                                    className="mt-2 w-full rounded-2xl border border-[#d9d9d9] bg-[#F9F9F9] px-4 py-3 text-sm font-bold text-[#414141] outline-none transition focus:border-red-300 focus:bg-white focus:ring-4 focus:ring-red-100 disabled:opacity-60"
                                    placeholder={DELETE_CONFIRMATION_PHRASE}
                                />
                                {deleteError && <p className="mt-3 text-xs font-bold text-red-600">{deleteError}</p>}
                                <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                                    <button
                                        type="button"
                                        onClick={closeDeleteDialog}
                                        disabled={deleteBusy}
                                        className="inline-flex min-h-[42px] items-center justify-center rounded-full border border-black/10 bg-white px-5 text-[11px] font-black uppercase tracking-[0.14em] text-black/60 transition hover:bg-slate-50 disabled:opacity-60"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={deleteBusy || !canConfirmAccountDelete}
                                        className="inline-flex min-h-[42px] items-center justify-center rounded-full bg-red-600 px-5 text-[11px] font-black uppercase tracking-[0.14em] text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-red-200 disabled:text-red-500"
                                    >
                                        {deleteBusy ? 'Deleting...' : 'Delete My Data'}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                )}

                {!isAuthenticated ? (
                    <section className="rounded-2xl border border-[#d9d9d9] bg-white p-6 shadow-sm">
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                            <div>
                                <p className="text-lg font-black text-[#414141]">Verify your phone</p>
                                <p className="mt-1 text-sm font-medium leading-relaxed text-[#414141]">
                                    Sign in to see your profile, vehicles, uploaded files, and generated reports.
                                </p>
                            </div>
                            <button
                                onClick={openAuthModal}
                                className="inline-flex min-h-[44px] items-center justify-center rounded-full bg-[#D81F26] px-6 text-sm font-black text-white shadow-[0_6px_16px_rgba(216,31,38,0.22)] transition hover:bg-[#B8181E] active:scale-[0.98]"
                            >
                                Login
                            </button>
                        </div>
                    </section>
                ) : (
                    <div className="space-y-4">
                        <section className="card-container rounded-2xl border border-[#d9d9d9] bg-white p-4 shadow-sm card-wide:p-5">
                            <div className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-4 gap-y-4 card-wide:grid-cols-[auto_minmax(0,1fr)_auto]">
                                    <ProfilePhotoAvatar editor={photoEditor} profileLoading={profileLoading} />
                                    <div className="min-w-0">
                                        {profileLoading && !profileName && !nameEditor.editing ? (
                                            <SkeletonBlock className="h-5 w-40 rounded-xl" />
                                        ) : (
                                            <ProfileNameField editor={nameEditor} displayName={accountLabel} />
                                        )}
                                        {accountSubLabel && (
                                            <p className="mt-1 truncate text-sm font-semibold text-[#414141]">{accountSubLabel}</p>
                                        )}
                                        <div className="mt-2 flex flex-wrap items-center gap-2">
                                            <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-black text-emerald-700">Verified</span>
                                            <span className="rounded-full bg-[#F9F9F9] px-2.5 py-1 text-[11px] font-black text-[#414141]">{planLabel}</span>
                                        </div>
                                        <ProfilePhotoActions editor={photoEditor} />
                                    </div>
                                <div className="col-span-2 grid grid-cols-2 gap-2 card-wide:col-span-1 card-wide:grid-cols-1 card-wide:gap-2.5">
                                    {nameEditor.editing ? (
                                        <>
                                            <button
                                                type="button"
                                                onClick={nameEditor.save}
                                                disabled={nameEditor.saving}
                                                className="inline-flex min-h-[42px] items-center justify-center rounded-full bg-[#D81F26] px-5 text-sm font-black text-white shadow-sm transition hover:bg-[#B8181E] active:scale-[0.99] disabled:opacity-60 card-wide:min-h-[40px]"
                                            >
                                                {nameEditor.saving ? 'Saving...' : 'Save'}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={nameEditor.cancel}
                                                disabled={nameEditor.saving}
                                                className="inline-flex min-h-[42px] items-center justify-center rounded-full border border-[#d9d9d9] bg-white px-5 text-sm font-black text-[#414141] shadow-sm transition hover:bg-[#F9F9F9] disabled:opacity-60 card-wide:min-h-[40px]"
                                            >
                                                Cancel
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <button
                                                ref={nameEditor.editButtonRef}
                                                type="button"
                                                onClick={nameEditor.start}
                                                disabled={profileLoading && !profileName}
                                                aria-label={profileName ? 'Edit name' : 'Add name'}
                                                className="inline-flex min-h-[42px] items-center justify-center gap-1.5 rounded-full border border-[#d9d9d9] bg-white px-5 text-sm font-black text-[#414141] shadow-sm transition hover:border-[#D81F26] hover:text-[#D81F26] disabled:opacity-60 card-wide:min-h-[40px]"
                                            >
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                                    <path d="M12 20h9" />
                                                    <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
                                                </svg>
                                                {profileName ? 'Edit' : 'Add'}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={onLogout}
                                                className="inline-flex min-h-[42px] items-center justify-center rounded-full border border-[#d9d9d9] bg-white px-5 text-sm font-black text-[#414141] shadow-sm transition hover:bg-[#F9F9F9] card-wide:min-h-[40px]"
                                            >
                                                Sign Out
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>

                            <div data-tooltip-boundary className="mt-5 divide-y divide-[#d9d9d9] overflow-hidden rounded-2xl border border-[#ececec] bg-white">
                                <ProfileDetailsEditor
                                    bare
                                    identity={identity}
                                    region={profile?.region || ''}
                                    profileLoading={profileLoading}
                                    onSaved={fetchProfile}
                                />
                            </div>

                            <div className="mt-5 overflow-hidden rounded-2xl border border-[#ececec] bg-white">
                                <div className="grid gap-1 px-4 py-3 card-wide:grid-cols-[150px_1fr] card-wide:items-center">
                                    <p className="text-sm font-black text-[#414141]">Active vehicle</p>
                                    <p className="truncate text-sm font-semibold text-[#414141]">
                                        {profileLoading ? 'Loading...' : getVehicleLabel(activeVehicle)}
                                    </p>
                                </div>
                            </div>

                            <EmailAddressCard email={profile?.email ?? email} onSaved={fetchProfile} />
                            <PhoneNumberCard phone={profile?.phone_number ?? phone} onSaved={fetchProfile} />
                        </section>

                        <section aria-labelledby="profile-preferences-consents-heading" className="space-y-4">
                            <div className="px-1 pt-2">
                                <h2 id="profile-preferences-consents-heading" className="text-xl font-black text-[#071426]">Preferences &amp; Consents</h2>
                                <p className="mt-1 text-sm font-medium text-[#5f748b]">Manage your preferences and view your recorded consent.</p>
                            </div>
                            <section data-tooltip-boundary className="rounded-2xl border border-[#d8e6f3] bg-white p-5 shadow-sm">
                                <h3 className="mb-2 text-lg font-black text-[#071426]">Shop search</h3>
                                <ShopSearchRadiusSelector
                                    identity={identity}
                                    savedRadius={profile?.shop_search_radius_miles}
                                    profileLoading={profileLoading}
                                    onSaved={fetchProfile}
                                />
                            </section>
                            <DiyKnowledgeLevelCard phone={identity} isAuthenticated={isAuthenticated} />

                            {/* Unit preferences hidden: units are fixed to US (miles, °F, gallons) for now.
                                Uncomment to bring the switch back.
                            <UnitPreferencesCard
                                phone={identity}
                                isAuthenticated={isAuthenticated}
                                profile={profile}
                                onSaved={fetchProfile}
                            />
                            */}

                            <NotificationsCard />
                            <MarketingPreferencesCard key={identity} identity={identity} onSessionExpired={refreshAuthState} />
                            <ConsentStatusCard key={`consents-${identity}`} identity={identity} onSessionExpired={refreshAuthState} />
                        </section>

                        {/* Saved data section hidden. Uncomment to bring it back.
                        <section className="rounded-2xl border border-[#d9d9d9] bg-white p-5 shadow-sm">
                            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                                <div>
                                    <p className="text-lg font-black text-[#414141]">Saved data</p>
                                    <p className="mt-1 text-sm font-medium text-[#414141]">Your vehicles, uploads, and generated reports.</p>
                                </div>
                                <div className="grid grid-cols-3 gap-2 sm:min-w-[280px]">
                                    {[
                                        ['Vehicles', profileLoading ? '...' : vehicleCount],
                                        ['Uploads', loading ? '...' : uploadsCount],
                                        ['Reports', loading ? '...' : reportsCount],
                                    ].map(([label, value]) => (
                                        <div key={label} className="rounded-xl bg-[#F9F9F9] px-3 py-2 text-center">
                                            <p className="text-lg font-black text-[#414141]">{value}</p>
                                            <p className="text-[10px] font-black uppercase tracking-[0.12em] text-[#7f7f7f]">{label}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </section>
                        */}

                        {error && (
                            <p className="rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-xs font-bold text-red-600">
                                {error}
                            </p>
                        )}
                    </div>
                )}

                <div className="mt-4">
                    <AddToHomeScreenCard />
                </div>

                <section className="card-container mt-4 rounded-2xl border border-[#d9d9d9] bg-white p-5 shadow-sm">
                    <div className="flex flex-col gap-4 card-wide:flex-row card-wide:items-center card-wide:justify-between">
                        <div>
                            <p className="text-lg font-black text-[#414141]">Help and resources</p>
                        </div>
                        <div className="grid gap-2 card-wide:grid-cols-2">
                            <button
                                onClick={() => navigate('/contact')}
                                className="inline-flex min-h-[42px] items-center justify-center rounded-full border border-[#d9d9d9] bg-[#F9F9F9] px-5 text-sm font-black text-[#414141] transition hover:bg-white"
                            >
                                Contact Support
                            </button>
                            <button
                                onClick={() => navigate('/blogs')}
                                className="inline-flex min-h-[42px] items-center justify-center rounded-full border border-[#d9d9d9] bg-[#F9F9F9] px-5 text-sm font-black text-[#414141] transition hover:bg-white"
                            >
                                Read Blogs
                            </button>
                            <button
                                onClick={() => navigate('/terms')}
                                className="inline-flex min-h-[42px] items-center justify-center rounded-full border border-[#d9d9d9] bg-[#F9F9F9] px-5 text-sm font-black text-[#414141] transition hover:bg-white"
                            >
                                Terms
                            </button>
                            <button
                                onClick={() => navigate('/privacy')}
                                className="inline-flex min-h-[42px] items-center justify-center rounded-full border border-[#d9d9d9] bg-[#F9F9F9] px-5 text-sm font-black text-[#414141] transition hover:bg-white"
                            >
                                Privacy Policy
                            </button>
                        </div>
                    </div>
                </section>

                <section className="card-container mt-4 rounded-2xl border border-red-100 bg-white p-5 shadow-sm">
                    <div className="flex flex-col gap-4 card-wide:flex-row card-wide:items-center card-wide:justify-between">
                        <div>
                            <p className="text-lg font-black text-[#414141]">
                                {isAuthenticated ? 'Delete account data' : 'Clear browser session'}
                            </p>
                            <p className="mt-1 text-sm font-medium leading-relaxed text-[#414141]">
                                {isAuthenticated
                                    ? 'Removes saved vehicles, chats, diagnostics, reports, uploaded files, auth sessions, and browser keys.'
                                    : "Removes this browser's anonymous chat identity, local chat state, and session keys. It does not delete a verified account."}
                            </p>
                            {deleteError && isAuthenticated && <p className="mt-2 text-xs font-bold text-red-600">{deleteError}</p>}
                            {webUserId && !isAuthenticated && testerMode && <p className="mt-1 select-all font-mono text-[10px] text-black/25">{webUserId}</p>}
                        </div>
                        <div className="relative z-20 flex shrink-0 gap-2">
                            {isAuthenticated ? (
                                <button
                                    onClick={openDeleteDialog}
                                    disabled={deleteBusy}
                                    className="rounded-full border border-red-200 bg-red-50 px-5 py-2.5 text-sm font-black text-red-600 transition hover:bg-red-100 disabled:opacity-60"
                                >
                                    Delete my data
                                </button>
                            ) : (
                                <>
                                    {eraseConfirm && (
                                        <button
                                            onClick={() => setEraseConfirm(false)}
                                            className="rounded-full border border-black/10 bg-white px-5 py-2.5 text-sm font-black text-black/60 transition hover:bg-slate-50"
                                        >
                                            Cancel
                                        </button>
                                    )}
                                    <button
                                        onClick={handleErase}
                                        className={`rounded-full px-5 py-2.5 text-sm font-black transition ${
                                            eraseConfirm
                                                ? 'bg-red-600 text-white hover:bg-red-700'
                                                : 'border border-red-200 bg-red-50 text-red-600 hover:bg-red-100'
                                        }`}
                                    >
                                        {eraseConfirm ? 'Confirm clear' : 'Clear session'}
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </section>

                {testerMode && (
                    <section className="mt-5 rounded-2xl border border-amber-200 bg-amber-50 p-5">
                        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-amber-600">Tester mode</p>
                        <p className="mt-2 text-sm font-black text-[#414141]">
                            TesterMode <span className="font-mono text-amber-700">"{testerMode}"</span>
                        </p>
                        <p className="mt-1 text-xs font-medium text-[#414141]">
                            Conversations from this browser are <strong>not</strong> monitored while tester mode is active.
                        </p>
                    </section>
                )}

                {process.env.NODE_ENV === 'development' && isAuthenticated && (
                    <section className="mt-5 rounded-2xl border border-red-100 bg-red-50 p-5">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <div>
                                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-red-500">Development mode</p>
                                <p className="mt-2 text-sm font-black text-[#414141]">Erase Session Data</p>
                                <p className="mt-1 text-xs font-medium text-[#414141]">
                                    Removes your anonymous identity, chat history, and all locally stored session keys.
                                </p>
                                {webUserId && <p className="mt-1 select-all font-mono text-[10px] text-black/25">{webUserId}</p>}
                            </div>
                            <div className="relative z-20 flex shrink-0 gap-2">
                                {eraseConfirm && (
                                    <button
                                        onClick={() => setEraseConfirm(false)}
                                        className="rounded-full border border-black/10 bg-white px-5 py-2.5 text-[11px] font-black uppercase tracking-[0.14em] text-black/60 transition hover:bg-slate-50"
                                    >
                                        Cancel
                                    </button>
                                )}
                                <button
                                    onClick={handleErase}
                                    className={`rounded-full px-5 py-2.5 text-[11px] font-black uppercase tracking-[0.14em] transition ${
                                        eraseConfirm
                                            ? 'bg-red-600 text-white hover:bg-red-700'
                                            : 'border border-red-200 bg-white text-red-500 hover:bg-red-50'
                                    }`}
                                >
                                    {eraseConfirm ? 'Confirm Erase' : 'Erase Session Data'}
                                </button>
                            </div>
                        </div>
                    </section>
                )}
            </div>
        </div>
    );
};

export default SettingsPage;
